import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarMoeda } from '../services/formatters';
import TooltipInfo from '../components/TooltipInfo';
import GanttServicos from '../components/GanttServicos';
import { exportarCronograma, exportarRelatorioCompleto, exportarRelatorioFinanceiro, exportarOrcamento, exportarMateriais, exportarServicosMateriais } from '../services/exportarPDF';
import { temPermissao, temRestricao } from '../utils/permissoes';
import ArquivoPreview from '../components/ArquivoPreview';

const ICONE_EXT = { pdf: '📄', png: '🖼️', jpg: '🖼️', jpeg: '🖼️', dwg: '📐', dxf: '📐' };
const EXT_CAD = ['dwg', 'dxf'];

function formatarTamanho(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function permVerProjeto(ext) { return EXT_CAD.includes(ext) ? 'projetos_cad_ver' : 'projetos_doc_ver'; }
function permAnexarProjeto(ext) { return EXT_CAD.includes(ext) ? 'projetos_cad_anexar' : 'projetos_doc_anexar'; }
function permDeletarProjeto(ext) { return EXT_CAD.includes(ext) ? 'projetos_cad_deletar' : 'projetos_doc_deletar'; }

function labelStatus(status) {
  const labels = {
    orcamento: 'Orçamento',
    em_andamento: 'Em Andamento',
    em_andamento_alerta: 'Em Andamento',
    atrasada: 'Atrasada',
    concluida: 'Obra Entregue',
    concluida_pgto_pendente: 'Entregue · Pgto. Pendente',
    finalizada: 'Obra Finalizada',
    cancelada: 'Cancelada',
    pausada: 'Pausada',
    orcamento_recusado: 'Orç. Recusado',
  };
  return labels[status] || status;
}

function formatarDataLocal(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function CompraInlineCells({ m, compraLocal, setCompraLocal, salvarCompra, fornecedores, disabled, podeVerFinanceiro }) {
  const id = m.obra_material_id;
  const c = compraLocal[id] || { fornecedor_id: '', valor_compra: '', valor_compra_formatado: '', data_compra: '', data_entrega: '' };

  function update(field, value) {
    setCompraLocal(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  function handleValorCompra(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const n = parseFloat(raw) / 100;
    setCompraLocal(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        valor_compra: raw ? n : '',
        valor_compra_formatado: raw ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '',
      },
    }));
  }

  const inputStyle = { marginBottom: 0, fontSize: 12, padding: '3px 6px' };

  return (
    <>
      <td>
        <select
          value={c.fornecedor_id}
          onChange={e => update('fornecedor_id', e.target.value)}
          onBlur={() => salvarCompra(id, { ...c, fornecedor_id: c.fornecedor_id })}
          disabled={disabled}
          style={{ ...inputStyle, width: '100%', maxWidth: 155 }}
        >
          <option value="">Selecionar</option>
          {fornecedores.map(f => (
            <option key={f.clienteid} value={f.clienteid}>{f.nome_cliente}</option>
          ))}
        </select>
      </td>
      {podeVerFinanceiro && (
        <td style={{ textAlign: 'right' }}>
          <input
            type="text"
            value={c.valor_compra_formatado || ''}
            onChange={handleValorCompra}
            onBlur={() => salvarCompra(id, c)}
            disabled={disabled}
            placeholder="R$ 0,00"
            style={{ ...inputStyle, width: 110, textAlign: 'right' }}
          />
        </td>
      )}
      <td>
        <input
          type="date"
          value={c.data_compra}
          onChange={e => { update('data_compra', e.target.value); if (e.target.value) salvarCompra(id, { ...c, data_compra: e.target.value }); }}
          onBlur={e => salvarCompra(id, { ...c, data_compra: e.target.value || c.data_compra })}
          disabled={disabled}
          style={{ ...inputStyle, width: 130 }}
        />
      </td>
      <td>
        <input
          type="date"
          value={c.data_entrega}
          onChange={e => { update('data_entrega', e.target.value); if (e.target.value) salvarCompra(id, { ...c, data_entrega: e.target.value }); }}
          onBlur={e => salvarCompra(id, { ...c, data_entrega: e.target.value || c.data_entrega })}
          disabled={disabled}
          style={{ ...inputStyle, width: 130 }}
        />
      </td>
    </>
  );
}

function ObraDetalhe({ obraid, listaObras = [], onVoltar }) {
  const navigate = useNavigate();
  const apenasVerCronograma = temRestricao('obras_apenas_cronograma');
  const podeVerFinanceiro = !apenasVerCronograma;
  const [obra, setObra] = useState(null);
  const [pagamentoData, setPagamentoData] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [servicosDisponiveis, setServicosDisponiveis] = useState([]);
  const [formasPagamento, setFormasPagamento] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);

  const [mostrarFormServico, setMostrarFormServico] = useState(false);
  const [mostrarFormPagamento, setMostrarFormPagamento] = useState(false);
  const [mostrarFormRecebimento, setMostrarFormRecebimento] = useState(false);
  const [mostrarFormPrazo, setMostrarFormPrazo] = useState(false);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [mostrarFormIniciar, setMostrarFormIniciar] = useState(false);
  const [mostrarFormReabrir, setMostrarFormReabrir] = useState(false);
  const [creditoPrompt, setCreditoPrompt] = useState(null); // { saldoCreditos, precoCredito, mensagem, onUsar }
  const [mostrarFormCancelar, setMostrarFormCancelar] = useState(false);
  const [mostrarFormPausar, setMostrarFormPausar] = useState(false);
  const [mostrarFormReativar, setMostrarFormReativar] = useState(false);
  const [mostrarFormRecusar, setMostrarFormRecusar] = useState(false);
  const [justificativaCancelar, setJustificativaCancelar] = useState('');
  const [justificativaPausar, setJustificativaPausar] = useState('');
  const [justificativaRecusar, setJustificativaRecusar] = useState('');

  const [editandoObra, setEditandoObra] = useState(false);
  const [formEditObra, setFormEditObra] = useState({ nome_obra: '', valor_fechado_obra: '', valor_fechado_obra_formatado: '', justificativa: '', logradouro: '', bairro: '', cidade: '', estado: '', cep: '' });

  const [editandoObs, setEditandoObs] = useState(false);
  const [formObs, setFormObs] = useState('');
  const [menuPdfAberto, setMenuPdfAberto] = useState(false);
  const [modalPdfMateriais, setModalPdfMateriais] = useState({
    aberto: false,
    colunas: { unidade: true, quantidade: true, preco_unitario: true, total: true, fornecedor: false, valor_compra: false, data_compra: false, data_entrega: false },
  });
  const menuPdfRef = useRef(null);
  const salvarCompraTimerRef = useRef({});

  const [materiaisData, setMateriaisData] = useState({ materiais: [], total_materiais: 0 });
  const [configPDF, setConfigPDF] = useState({});
  const [fornecedores, setFornecedores] = useState([]);
  const [compraLocal, setCompraLocal] = useState({});
  const [buscaSinapi, setBuscaSinapi] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [itemSelecionado, setItemSelecionado] = useState(null);
  const [ufBusca, setUfBusca] = useState('');
  const [ufsDisponiveis, setUfsDisponiveis] = useState([]);
  const [formMaterial, setFormMaterial] = useState({ quantidade: '', preco_unitario: 0, preco_formatado: '', observacao: '', tipo_encargo: 'sem_desone' });
  const [temAnalitico, setTemAnalitico] = useState(false);
  const [composicaoAnalitica, setComposicaoAnalitica] = useState(null);
  const [itensAnalitico, setItensAnalitico] = useState([]);
  const [carregandoAnalitico, setCarregandoAnalitico] = useState(false);
  const [encargoAnalitico, setEncargoAnalitico] = useState('sem_desone');
  const [qtdComposicaoAnalitica, setQtdComposicaoAnalitica] = useState('1');
  const [itensAnaliticoSelecionados, setItensAnaliticoSelecionados] = useState({});
  const [editandoMaterial, setEditandoMaterial] = useState(null);
  const [mostrarFormMaterialManual, setMostrarFormMaterialManual] = useState(false);
  const [formManual, setFormManual] = useState({ descricao: '', unidade: '', quantidade: '', preco_unitario: 0, preco_formatado: '', observacao: '' });

  const [editandoServico, setEditandoServico] = useState(null);
  const [reabrindoServico, setReabrindoServico] = useState(null);
  const [justificativaReabrirServico, setJustificativaReabrirServico] = useState('');
  const [editandoPagamento, setEditandoPagamento] = useState(null);
  const [editandoRecebimento, setEditandoRecebimento] = useState(null);

  const [abaServicos, setAbaServicos] = useState('lista');
  const [abaObs, setAbaObs] = useState('obs');
  const [formAlterarPrazoServico, setFormAlterarPrazoServico] = useState({ obraservicoid: null, nova_data_inicio: '', nova_data_fim: '', visivel: false });
  const [confirmandoAtraso, setConfirmandoAtraso] = useState(null);
  const [avisoConcluirSemPgto, setAvisoConcluirSemPgto] = useState(false);

  const [formServico, setFormServico] = useState({ servicoid: '', valor_orcado_os: '', valor_orcado_os_formatado: '', descricao_servico_obra: '', data_inicio: '', data_fim: '', responsavel_id: '', predecessores: [] });
  const [avisoAdiantamento, setAvisoAdiantamento] = useState(null);
  const [confirmarExclusaoServico, setConfirmarExclusaoServico] = useState(null);
  const [formPagamento, setFormPagamento] = useState({ forma_pagamento: '', prazo_pagamento: '', valor_total: '', valor_total_formatado: '', parcelas: '', dia_vencimento: '' });
  const [formRecebimento, setFormRecebimento] = useState({ data_recebimento: '', valor_recebido: '', valor_recebido_formatado: '', observacao: '', forma_pagamento: '' });
  const [formPrazo, setFormPrazo] = useState({ novo_prazo: '', justificativa: '' });
  const [formIniciar, setFormIniciar] = useState({ prazo_combinado: '', valor_fechado_obra: '', valor_fechado_obra_formatado: '', logradouro: '', bairro: '', cidade: '', estado: '', cep: '' });
  const [justificativaReabrir, setJustificativaReabrir] = useState('');

  const [projetos, setProjetos] = useState([]);
  const [uploadandoProjeto, setUploadandoProjeto] = useState(false);
  const [previewProjeto, setPreviewProjeto] = useState(null);
  const [confirmDeleteProjeto, setConfirmDeleteProjeto] = useState(null);
  const inputProjetoRef = useRef(null);

  async function carregarProjetos() {
    if (!temPermissao('projetos_cad_ver') && !temPermissao('projetos_doc_ver')) return;
    try {
      const r = await api.get(`/obra-projetos/${obraid}`);
      setProjetos(r.data);
    } catch { }
  }

  async function handleUploadProjeto(e) {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('arquivo', file);
    setUploadandoProjeto(true);
    try {
      await api.post(`/obra-projetos/${obraid}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Arquivo anexado!');
      carregarProjetos();
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao enviar arquivo.');
    }
    setUploadandoProjeto(false);
    e.target.value = '';
  }

  async function abrirArquivo(p) {
    try {
      const res = await api.get(`/obra-projetos/arquivo/${p.projetoid}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      if (['dwg', 'dxf'].includes(p.extensao)) {
        a.download = p.nome_original;
      } else {
        a.target = '_blank';
        a.rel = 'noreferrer';
      }
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Erro ao abrir arquivo.');
    }
  }

  async function deletarProjeto(id) {
    try {
      await api.delete(`/obra-projetos/${id}`);
      toast.success('Arquivo removido.');
      setConfirmDeleteProjeto(null);
      carregarProjetos();
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao remover arquivo.');
    }
  }

  async function carregarMateriais() {
    try {
      const r = await api.get(`/obra-materiais/${obraid}`);
      setMateriaisData(r.data);
    } catch { }
  }

  async function carregarFornecedores() {
    try {
      const r = await api.get('/clientes?tipos=fornecedor');
      setFornecedores(r.data);
    } catch { }
  }

  function salvarCompra(id, values) {
    clearTimeout(salvarCompraTimerRef.current[id]);
    const snap = { ...values };
    salvarCompraTimerRef.current[id] = setTimeout(async () => {
      try {
        await api.put(`/obra-materiais/${id}/compra`, {
          fornecedor_id: snap.fornecedor_id ? parseInt(snap.fornecedor_id) : null,
          valor_compra: snap.valor_compra !== '' ? parseFloat(snap.valor_compra) : null,
          data_compra: snap.data_compra || null,
          data_entrega: snap.data_entrega || null,
        });
      } catch {
        toast.error('Erro ao salvar informações de compra.');
      }
    }, 100);
  }

  async function carregarUfsDisponiveis() {
    try {
      const r = await api.get('/sinapi/ufs-disponiveis');
      const ufs = r.data || [];
      setUfsDisponiveis(ufs);
      setUfBusca(prev => {
        if (prev) return prev;
        // pré-seleciona o estado da obra se disponível, senão o primeiro da lista
        const estadoObra = obra?.estado?.toUpperCase();
        if (estadoObra && ufs.includes(estadoObra)) return estadoObra;
        return ufs[0] || '';
      });
    } catch { }
    try {
      const r2 = await api.get('/sinapi/versao-ativa');
      setTemAnalitico(!!r2.data?.tem_analitico);
    } catch { }
  }

  async function carregarObra() {
    const response = await api.get(`/obras/${obraid}`);
    setObra(response.data);
  }

  async function carregarPagamento() {
    const response = await api.get(`/pagamentos/obra/${obraid}`);
    setPagamentoData(response.data);
  }

  async function carregarHistorico() {
    const response = await api.get(`/historico-prazos/${obraid}`);
    setHistorico(response.data);
  }

  async function carregarServicosDisponiveis() {
    const response = await api.get('/servicos');
    setServicosDisponiveis(response.data);
  }

  async function carregarResponsaveis() {
    try {
      const response = await api.get('/clientes?tipos=executor,construtor,terceiros');
      setResponsaveis(response.data);
    } catch { }
  }

  async function carregarFormasPagamento() {
    try {
      const response = await api.get('/formas-pagamento');
      setFormasPagamento(response.data);
    } catch {
      setFormasPagamento([
        { nome: 'Dinheiro', parcelado: false, tem_dia_vencimento: true },
        { nome: 'PIX à vista', parcelado: false, tem_dia_vencimento: true },
        { nome: 'PIX parcelado', parcelado: true, tem_dia_vencimento: true },
        { nome: 'Cartão crédito à vista', parcelado: false, tem_dia_vencimento: false },
        { nome: 'Cartão crédito parcelado', parcelado: true, tem_dia_vencimento: false },
        { nome: 'Boleto', parcelado: false, tem_dia_vencimento: false },
        { nome: 'Transferência', parcelado: false, tem_dia_vencimento: false },
      ]);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    carregarObra();
    carregarPagamento();
    carregarHistorico();
    carregarServicosDisponiveis();
    carregarResponsaveis();
    carregarFormasPagamento();
    carregarMateriais();
    carregarUfsDisponiveis();
    carregarProjetos();
    carregarFornecedores();
    api.get('/configuracoes-sistema').then(r => {
      const d = r.data;
      setConfigPDF({
        cor:          d.empresa_cor_primaria || null,
        empresa_nome: d.empresa_nome || null,
        cnpj:         d.empresa_cnpj || null,
        logo:         d.empresa_logo || null,
        logo_ratio:   parseFloat(d.empresa_logo_ratio || 3),
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const init = {};
    materiaisData.materiais.forEach(m => {
      const vc = m.valor_compra != null ? parseFloat(m.valor_compra) : null;
      const entry = {
        fornecedor_id: m.fornecedor_id ?? '',
        valor_compra: vc != null ? vc : '',
        valor_compra_formatado: vc > 0 ? vc.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '',
        data_compra: m.data_compra ? String(m.data_compra).split('T')[0] : '',
        data_entrega: m.data_entrega ? String(m.data_entrega).split('T')[0] : '',
      };
      init[m.obra_material_id] = entry;
    });
    setCompraLocal(init);
  }, [materiaisData]);

  useEffect(() => {
    function fecharMenu(e) {
      if (menuPdfRef.current && !menuPdfRef.current.contains(e.target)) {
        setMenuPdfAberto(false);
      }
    }
    document.addEventListener('mousedown', fecharMenu);
    return () => document.removeEventListener('mousedown', fecharMenu);
  }, []);

  function getFormaFlags(nomeForma) {
    return formasPagamento.find(f => f.nome === nomeForma) || { parcelado: false, tem_dia_vencimento: false };
  }

  function handleValorServico(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setFormServico({ ...formServico, valor_orcado_os: numero || 0, valor_orcado_os_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function handleValorFechado(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setFormIniciar({ ...formIniciar, valor_fechado_obra: numero || 0, valor_fechado_obra_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function handleValorFechadoEdicao(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setFormEditObra({ ...formEditObra, valor_fechado_obra: numero || 0, valor_fechado_obra_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function handleValorPagamento(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setFormPagamento({ ...formPagamento, valor_total: numero || 0, valor_total_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function handleValorRecebimento(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setFormRecebimento({ ...formRecebimento, valor_recebido: numero || 0, valor_recebido_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function handleValorEdicaoServico(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setEditandoServico({ ...editandoServico, valor_orcado_os: numero || 0, valor_orcado_os_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  function renderBadgeServico(status, estaAtrasado) {
    if (estaAtrasado && status !== 'concluido') return <span className="badge badge-atrasada">Atrasado</span>;
    const configs = {
      pendente:     { classe: 'badge badge-orcamento',    label: 'Pendente' },
      em_andamento: { classe: 'badge badge-em-andamento', label: 'Em Andamento' },
      concluido:    { classe: 'badge badge-concluida',    label: 'Concluído' },
      atrasado:     { classe: 'badge badge-atrasada',     label: 'Atrasado' },
    };
    const config = configs[status] || { classe: 'badge', label: status };
    return <span className={config.classe}>{config.label}</span>;
  }

  async function adicionarServico() {
    if (!formServico.servicoid) { toast.error('Selecione um serviço!'); return; }
    if (!formServico.responsavel_id) { toast.error('Responsável pelo serviço é obrigatório!'); return; }
    const emOrcamento = obra.status_obra === 'orcamento';
    if (!emOrcamento && !formServico.data_inicio) { toast.error('Data de início é obrigatória!'); return; }
    if (!emOrcamento && !formServico.data_fim) { toast.error('Data fim prevista é obrigatória!'); return; }
    if (!formServico.valor_orcado_os) { toast.error('Valor orçado é obrigatório!'); return; }
    if (formServico.data_inicio && formServico.data_fim && new Date(formServico.data_fim) < new Date(formServico.data_inicio)) {
      toast.error('A data fim não pode ser anterior à data início!');
      return;
    }
    if (formServico.predecessores.length > 0 && formServico.data_inicio) {
      const maxFimStr = obra.servicos
        .filter(s => formServico.predecessores.map(Number).includes(Number(s.obraservicoid)) && s.data_fim)
        .reduce((max, s) => { const d = new Date(s.data_fim).toISOString().split('T')[0]; return d > max ? d : max; }, '');
      if (maxFimStr && formServico.data_inicio <= maxFimStr) {
        toast.error(`Data de início deve ser após o término do predecessor (${maxFimStr.split('-').reverse().join('/')}).`);
        return;
      }
    }
    try {
      await api.post('/obras-servicos', {
        obraid,
        servicoid: formServico.servicoid,
        valor_orcado_os: formServico.valor_orcado_os || 0,
        descricao_servico_obra: formServico.descricao_servico_obra,
        data_inicio: formServico.data_inicio || null,
        data_fim: formServico.data_fim || null,
        responsavel_id: formServico.responsavel_id,
        predecessores: formServico.predecessores,
      });
      setFormServico({ servicoid: '', valor_orcado_os: '', valor_orcado_os_formatado: '', descricao_servico_obra: '', data_inicio: '', data_fim: '', responsavel_id: '', predecessores: [] });
      setMostrarFormServico(false);
      await carregarObra();
      toast.success('Serviço adicionado!');
      if (obra.valor_fechado_obra > 0) {
        toast('⚠️ Um serviço foi adicionado. Revise o valor fechado da obra!', { duration: 7000, style: { background: '#fff3cd', color: '#856404', fontWeight: 600 } });
      }
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao adicionar serviço.');
    }
  }

  async function iniciarServico(id) {
    try {
      await api.put(`/obras-servicos/${id}/iniciar`);
      carregarObra();
      toast.success('Serviço iniciado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao iniciar serviço.');
    }
  }

  async function concluirServico(id) {
    const res = await api.put(`/obras-servicos/${id}/concluir`);
    carregarObra();
    toast.success('Serviço concluído!');
    if (res.data?.adiantamento?.possivel) {
      setAvisoAdiantamento({ servicoid: id, ...res.data.adiantamento });
    }
  }

  async function reabrirServico(id) {
    await api.put(`/obras-servicos/${id}/reabrir`, { justificativa: justificativaReabrirServico });
    setReabrindoServico(null);
    setJustificativaReabrirServico('');
    carregarObra();
    carregarHistorico();
    toast.success('Serviço reaberto!');
  }

  async function cancelarObra() {
    if (!justificativaCancelar.trim()) { toast.error('Justificativa é obrigatória para cancelar!'); return; }
    try {
      await api.put(`/obras/${obraid}/cancelar`, { justificativa: justificativaCancelar });
      setMostrarFormCancelar(false); setJustificativaCancelar('');
      carregarObra(); carregarHistorico();
      toast.success('Obra cancelada!');
    } catch (err) { toast.error(err.response?.data?.mensagem || 'Erro ao cancelar.'); }
  }

  async function pausarObra() {
    if (!justificativaPausar.trim()) { toast.error('Justificativa é obrigatória para pausar!'); return; }
    try {
      await api.put(`/obras/${obraid}/pausar`, { justificativa: justificativaPausar });
      setMostrarFormPausar(false); setJustificativaPausar('');
      carregarObra(); carregarHistorico();
      toast.success('Obra pausada!');
    } catch (err) { toast.error(err.response?.data?.mensagem || 'Erro ao pausar.'); }
  }

  async function reativarObra(usar_credito) {
    try {
      const res = await api.put(`/obras/${obraid}/reativar`, usar_credito ? { usar_credito: true } : {});
      setMostrarFormReativar(false);
      setCreditoPrompt(null);
      carregarObra(); carregarHistorico();
      toast.success(res.data?.mensagem || 'Obra reativada!');
    } catch (err) {
      const data = err.response?.data;
      if (data?.pode_usar_credito !== undefined) {
        setCreditoPrompt({
          saldoCreditos: data.saldo_creditos,
          precoCredito: data.preco_credito,
          mensagem: data.mensagem,
          onUsar: data.saldo_creditos > 0 ? () => reativarObra(true) : null,
        });
        return;
      }
      toast.error(data?.mensagem || 'Erro ao reativar.');
    }
  }

  async function recusarOrcamento() {
    try {
      await api.put(`/obras/${obraid}/recusar`, { justificativa: justificativaRecusar });
      setMostrarFormRecusar(false); setJustificativaRecusar('');
      carregarObra(); carregarHistorico();
      toast.success('Orçamento marcado como recusado!');
    } catch (err) { toast.error(err.response?.data?.mensagem || 'Erro ao recusar orçamento.'); }
  }

  function iniciarEdicaoServico(s) {
    setEditandoServico({
      obraservicoid: s.obraservicoid,
      valor_orcado_os: parseFloat(s.valor_orcado_os || 0),
      valor_orcado_os_formatado: parseFloat(s.valor_orcado_os || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      descricao_servico_obra: s.descricao_servico_obra || '',
      responsavel_id: s.responsavel_id || '',
      predecessores: (s.predecessores || []).map(p => Number(p.predecessor_id)),
    });
  }

  async function salvarEdicaoServico() {
    const servicoAtual = obra.servicos.find(s => s.obraservicoid === editandoServico.obraservicoid);
    const totalAntes = obra.total_orcado || 0;
    const diferenca = (editandoServico.valor_orcado_os || 0) - parseFloat(servicoAtual.valor_orcado_os || 0);

    if (!editandoServico.responsavel_id) { toast.error('Responsável pelo serviço é obrigatório!'); return; }

    if ((editandoServico.predecessores || []).length > 0) {
      const dataInicio = String(servicoAtual.data_inicio || '').split('T')[0];
      const maxFimStr = obra.servicos
        .filter(s => (editandoServico.predecessores || []).map(Number).includes(Number(s.obraservicoid)) && s.data_fim)
        .reduce((max, s) => { const d = new Date(s.data_fim).toISOString().split('T')[0]; return d > max ? d : max; }, '');
      if (dataInicio && maxFimStr && dataInicio <= maxFimStr) {
        toast.error(`O serviço inicia em ${dataInicio.split('-').reverse().join('/')} mas o predecessor só termina em ${maxFimStr.split('-').reverse().join('/')}. Ajuste as datas antes de vincular.`);
        return;
      }
    }

    try {
      await api.put(`/obras-servicos/${editandoServico.obraservicoid}`, {
        valor_orcado_os: editandoServico.valor_orcado_os,
        valor_cobrado_os: servicoAtual.valor_cobrado_os,
        descricao_servico_obra: editandoServico.descricao_servico_obra,
        responsavel_id: editandoServico.responsavel_id,
        predecessores: editandoServico.predecessores || [],
      });
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao salvar serviço.');
      return;
    }
    setEditandoServico(null);
    await carregarObra();
    toast.success('Serviço atualizado!');

    if (diferenca !== 0 && obra.valor_fechado_obra) {
      const novoTotal = totalAntes + diferenca;
      toast(`⚠️ Total de serviços mudou para ${formatarMoeda(novoTotal)}. Revise o valor fechado da obra!`, {
        duration: 8000,
        style: { background: '#fff3cd', color: '#856404', fontWeight: 600 },
      });
    }
  }

  async function alterarPrazoServico(id) {
    if (!formAlterarPrazoServico.nova_data_fim) { toast.error('Informe a nova data fim!'); return; }
    if (formAlterarPrazoServico.nova_data_inicio && formAlterarPrazoServico.nova_data_fim < formAlterarPrazoServico.nova_data_inicio) {
      toast.error('A data fim não pode ser anterior à data início!'); return;
    }

    const novoInicio = formAlterarPrazoServico.nova_data_inicio;
    if (novoInicio) {
      const servicoAtual = obra.servicos.find(s => Number(s.obraservicoid) === Number(id));
      const predecessores = servicoAtual?.predecessores || [];
      if (predecessores.length > 0) {
        const maxFimPred = obra.servicos
          .filter(s => predecessores.map(p => Number(p.predecessor_id)).includes(Number(s.obraservicoid)) && s.data_fim)
          .reduce((max, s) => {
            const d = new Date(s.data_fim).toISOString().split('T')[0];
            return d > max ? d : max;
          }, '');
        if (maxFimPred && novoInicio <= maxFimPred) {
          toast.error(`Data de início (${novoInicio.split('-').reverse().join('/')}) não pode ser antes do término do predecessor (${maxFimPred.split('-').reverse().join('/')}).`);
          return;
        }
      }
    }

    try {
      await api.put(`/obras-servicos/${id}/prazo`, {
        nova_data_fim: formAlterarPrazoServico.nova_data_fim,
        nova_data_inicio: formAlterarPrazoServico.nova_data_inicio || undefined,
      });
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao alterar prazo.');
      return;
    }
    setFormAlterarPrazoServico({ obraservicoid: null, nova_data_inicio: '', nova_data_fim: '', visivel: false });
    carregarObra();
    carregarHistorico();
    toast.success('Prazo do serviço atualizado!');
  }

  async function registrarAtrasoServico(id) {
    const d = new Date();
    const hoje = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    try {
      await api.put(`/obras-servicos/${id}/prazo`, { nova_data_fim: hoje });
      setConfirmandoAtraso(null);
      carregarObra();
      carregarHistorico();
      toast.success('Atraso registrado e prazos atualizados!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao registrar atraso.');
    }
  }

  async function deletarServico(id) {
    try {
      const { data: dependentes } = await api.get(`/obras-servicos/${id}/dependentes`);
      if (dependentes.length > 0) {
        setConfirmarExclusaoServico({ id, dependentes });
        return;
      }
    } catch (_) {}
    await executarExclusaoServico(id);
  }

  async function executarExclusaoServico(id) {
    await api.delete(`/obras-servicos/${id}`);
    setConfirmarExclusaoServico(null);
    await carregarObra();
    toast.success('Serviço removido!');
    if (obra.valor_fechado_obra > 0) {
      toast('⚠️ Um serviço foi removido. Revise o valor fechado da obra!', { duration: 7000, style: { background: '#fff3cd', color: '#856404', fontWeight: 600 } });
    }
  }

  async function iniciarObra() {
    if (!obra.servicos || obra.servicos.length === 0) {
      toast.error('Adicione pelo menos um serviço antes de iniciar a obra!', { duration: 5000 });
      return;
    }
    if (!formIniciar.prazo_combinado) { toast.error('Prazo combinado é obrigatório para iniciar!'); return; }
    if (!formIniciar.valor_fechado_obra || formIniciar.valor_fechado_obra <= 0) {
      toast.error('Valor fechado é obrigatório para iniciar a obra!'); return;
    }
    try {
      await api.put(`/obras/${obraid}/iniciar`, formIniciar);
      setMostrarFormIniciar(false);
      setFormIniciar({ prazo_combinado: '', valor_fechado_obra: '', valor_fechado_obra_formatado: '', logradouro: '', bairro: '', cidade: '', estado: '', cep: '' });
      carregarObra();
      toast.success('Obra iniciada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao iniciar a obra.', { duration: 6000 });
    }
  }

  function pedirConfirmacaoConcluir() {
    const semPagamento = !pagamentoData?.pagamentos?.length;
    if (semPagamento) {
      setAvisoConcluirSemPgto(true);
    } else {
      concluirObra();
    }
  }

  async function concluirObra() {
    setAvisoConcluirSemPgto(false);
    try {
      await api.put(`/obras/${obraid}/concluir`);
      carregarObra();
      toast.success('Obra concluída com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao concluir a obra.', { duration: 6000 });
    }
  }

  async function reabrirObra(usar_credito) {
    try {
      const res = await api.put(`/obras/${obraid}/reabrir`, { justificativa: justificativaReabrir, ...(usar_credito ? { usar_credito: true } : {}) });
      setMostrarFormReabrir(false);
      setJustificativaReabrir('');
      setCreditoPrompt(null);
      carregarObra();
      carregarHistorico();
      toast.success(res.data?.mensagem || 'Obra reaberta com sucesso!');
    } catch (err) {
      const data = err.response?.data;
      if (data?.pode_usar_credito !== undefined) {
        setCreditoPrompt({
          saldoCreditos: data.saldo_creditos,
          precoCredito: data.preco_credito,
          mensagem: data.mensagem,
          onUsar: data.saldo_creditos > 0 ? () => reabrirObra(true) : null,
        });
        return;
      }
      toast.error(data?.mensagem || 'Erro ao reabrir a obra.');
    }
  }

  async function alterarPrazo() {
    if (!formPrazo.novo_prazo) {
      toast.error('Novo prazo é obrigatório!'); return;
    }
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    if (new Date(formPrazo.novo_prazo) < hoje) {
      toast.error('O novo prazo não pode ser anterior à data de hoje!'); return;
    }
    try {
      await api.put(`/obras/${obraid}/alterar-prazo`, formPrazo);
      setFormPrazo({ novo_prazo: '', justificativa: '' });
      setMostrarFormPrazo(false);
      carregarObra();
      carregarHistorico();
      toast.success('Prazo alterado com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao alterar prazo.', { duration: 6000 });
    }
  }

  function iniciarEdicaoObra() {
    setFormEditObra({
      nome_obra: obra.nome_obra,
      valor_fechado_obra: parseFloat(obra.valor_fechado_obra || 0),
      valor_fechado_obra_formatado: parseFloat(obra.valor_fechado_obra || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      justificativa: '',
      logradouro: obra.logradouro || '',
      bairro: obra.bairro || '',
      cidade: obra.cidade || '',
      estado: obra.estado || '',
      cep: obra.cep || '',
    });
    setEditandoObra(true);
  }

  function formatarCep(valor) {
    const n = valor.replace(/\D/g, '').slice(0, 8);
    return n.length > 5 ? `${n.slice(0, 5)}-${n.slice(5)}` : n;
  }

  async function buscarCep(cepValor, setForm) {
    const cepLimpo = cepValor.replace(/\D/g, '');
    if (cepLimpo.length !== 8) { toast.error('Digite um CEP válido com 8 dígitos!'); return; }
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (data.erro) { toast.error('CEP não encontrado!'); return; }
      setForm(f => ({
        ...f,
        logradouro: data.logradouro || f.logradouro,
        bairro: data.bairro || f.bairro,
        cidade: data.localidade || f.cidade,
        estado: data.uf || f.estado,
      }));
      toast.success('Endereço preenchido!');
    } catch {
      toast.error('Erro ao buscar CEP. Verifique sua conexão.');
    }
  }

  async function salvarEdicaoObra() {
    try {
      await api.put(`/obras/${obraid}`, {
        nome_obra: formEditObra.nome_obra,
        valor_fechado_obra: formEditObra.valor_fechado_obra,
        justificativa: formEditObra.justificativa,
        logradouro: formEditObra.logradouro,
        bairro: formEditObra.bairro,
        cidade: formEditObra.cidade,
        estado: formEditObra.estado,
        cep: formEditObra.cep,
      });
      setEditandoObra(false);
      carregarObra();
      toast.success('Obra atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao atualizar a obra.', { duration: 6000 });
    }
  }

  async function buscarSinapiItems(termo, uf) {
    const ufFinal = uf ?? ufBusca;
    if (!termo || termo.trim().length < 2) { setResultadosBusca([]); return; }
    if (!ufFinal) { setResultadosBusca([]); return; }
    setBuscando(true);
    try {
      const filtroTipo = formMaterial.tipo_encargo === 'analitico' ? '&tipo=composicao' : '';
      const r = await api.get(`/sinapi/buscar?q=${encodeURIComponent(termo)}&uf=${ufFinal}${filtroTipo}`);
      setResultadosBusca(r.data);
    } catch { setResultadosBusca([]); }
    finally { setBuscando(false); }
  }

  function selecionarItemSinapi(item) {
    if (formMaterial.tipo_encargo === 'analitico') {
      selecionarComposicaoAnalitica(item);
      return;
    }
    const campoPreco = `preco_${formMaterial.tipo_encargo}`;
    const preco = parseFloat(item[campoPreco] ?? 0) || 0;
    setItemSelecionado(item);
    setFormMaterial(f => ({
      ...f,
      preco_unitario: preco,
      preco_formatado: preco > 0 ? preco.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '',
      quantidade: '',
    }));
    setResultadosBusca([]);
    setBuscaSinapi('');
  }

  async function carregarItensAnalitico(codigo, uf, encargo) {
    setCarregandoAnalitico(true);
    try {
      const r = await api.get(`/sinapi/composicoes/${codigo}/itens?uf=${uf}&tipo_encargo=${encargo}`);
      setItensAnalitico(r.data || []);
      const marcados = {};
      (r.data || []).forEach(it => { marcados[it.item_codigo] = true; });
      setItensAnaliticoSelecionados(marcados);
    } catch {
      toast.error('Erro ao carregar itens da composição.');
      setItensAnalitico([]);
    } finally {
      setCarregandoAnalitico(false);
    }
  }

  function selecionarComposicaoAnalitica(item) {
    setComposicaoAnalitica(item);
    setQtdComposicaoAnalitica('1');
    setResultadosBusca([]);
    setBuscaSinapi('');
    carregarItensAnalitico(item.codigo, ufBusca, encargoAnalitico);
  }

  function toggleItemAnalitico(codigo) {
    setItensAnaliticoSelecionados(prev => ({ ...prev, [codigo]: !prev[codigo] }));
  }

  async function adicionarItensAnalitico() {
    const qtd = parseFloat(qtdComposicaoAnalitica);
    if (!qtd || qtd <= 0) { toast.error('Informe a quantidade da composição.'); return; }
    const selecionados = itensAnalitico.filter(it => itensAnaliticoSelecionados[it.item_codigo]);
    if (!selecionados.length) { toast.error('Selecione ao menos um item.'); return; }
    try {
      for (const it of selecionados) {
        await api.post('/obra-materiais', {
          obraid,
          tipo: it.tipo_item === 'INSUMO' ? 'insumo' : 'composicao',
          codigo_sinapi: String(it.item_codigo),
          descricao: it.descricao,
          unidade: it.unidade,
          uf: ufBusca,
          tipo_encargo: encargoAnalitico,
          quantidade: (it.coeficiente || 0) * qtd,
          preco_unitario: it.preco_unitario || 0,
          observacao: `Item da composição SINAPI ${composicaoAnalitica.codigo} (Analítico)`,
        });
      }
      toast.success(`${selecionados.length} item(ns) adicionado(s)!`);
      setComposicaoAnalitica(null);
      setItensAnalitico([]);
      setItensAnaliticoSelecionados({});
      carregarMateriais();
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao adicionar itens.');
    }
  }

  function handlePrecoBusca(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const n = parseFloat(raw) / 100;
    setFormMaterial(f => ({ ...f, preco_unitario: n || 0, preco_formatado: raw ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' }));
  }

  function handlePrecoManual(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const n = parseFloat(raw) / 100;
    setFormManual(f => ({ ...f, preco_unitario: n || 0, preco_formatado: raw ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' }));
  }

  async function adicionarMaterialSinapi() {
    if (!itemSelecionado) { toast.error('Selecione um item SINAPI.'); return; }
    if (!formMaterial.quantidade || parseFloat(formMaterial.quantidade) <= 0) { toast.error('Informe a quantidade.'); return; }
    try {
      await api.post('/obra-materiais', {
        obraid,
        tipo: itemSelecionado.tipo,
        sinapi_versao_id: null,
        codigo_sinapi: String(itemSelecionado.codigo),
        descricao: itemSelecionado.descricao,
        unidade: itemSelecionado.unidade,
        uf: itemSelecionado.uf || obra?.estado || null,
        tipo_encargo: formMaterial.tipo_encargo,
        quantidade: parseFloat(formMaterial.quantidade),
        preco_unitario: formMaterial.preco_unitario,
        observacao: formMaterial.observacao,
      });
      setItemSelecionado(null);
      setFormMaterial({ quantidade: '', preco_unitario: '', preco_formatado: '', observacao: '', tipo_encargo: 'sem_desone' });
      setBuscaSinapi('');
      carregarMateriais();
      toast.success('Material adicionado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao adicionar material.');
    }
  }

  async function adicionarMaterialManual() {
    if (!formManual.descricao.trim()) { toast.error('Descrição é obrigatória.'); return; }
    if (!formManual.quantidade || parseFloat(formManual.quantidade) <= 0) { toast.error('Informe a quantidade.'); return; }
    try {
      await api.post('/obra-materiais', {
        obraid,
        tipo: 'manual',
        descricao: formManual.descricao,
        unidade: formManual.unidade,
        quantidade: parseFloat(formManual.quantidade),
        preco_unitario: formManual.preco_unitario || 0,
        observacao: formManual.observacao,
      });
      setFormManual({ descricao: '', unidade: '', quantidade: '', preco_unitario: '', preco_formatado: '', observacao: '' });
      setMostrarFormMaterialManual(false);
      carregarMateriais();
      toast.success('Material adicionado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao adicionar material.');
    }
  }

  async function salvarEdicaoMaterial() {
    if (!editandoMaterial.quantidade || parseFloat(editandoMaterial.quantidade) <= 0) { toast.error('Quantidade inválida.'); return; }
    try {
      await api.put(`/obra-materiais/${editandoMaterial.obra_material_id}`, {
        descricao: editandoMaterial.descricao,
        unidade: editandoMaterial.unidade,
        quantidade: parseFloat(editandoMaterial.quantidade),
        preco_unitario: editandoMaterial.preco_unitario,
        observacao: editandoMaterial.observacao,
      });
      setEditandoMaterial(null);
      carregarMateriais();
      toast.success('Material atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao atualizar material.');
    }
  }

  async function removerMaterial(id) {
    try {
      await api.delete(`/obra-materiais/${id}`);
      carregarMateriais();
      toast.success('Material removido!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao remover material.');
    }
  }

  function handlePrecoEdicaoMaterial(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const n = parseFloat(raw) / 100;
    setEditandoMaterial(m => ({ ...m, preco_unitario: n || 0, preco_formatado: raw ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' }));
  }

  function labelEncargo(t) {
    return { sem_desone: 'SD', com_desone: 'CD', sem_encargos: 'SE' }[t] || '';
  }

  async function salvarObservacoes() {
    if (formObs.length > 3000) { toast.error('Máximo de 3000 caracteres!'); return; }
    try {
      await api.put(`/obras/${obraid}`, {
        nome_obra: obra.nome_obra,
        valor_fechado_obra: obra.valor_fechado_obra,
        logradouro: obra.logradouro,
        bairro: obra.bairro,
        cidade: obra.cidade,
        estado: obra.estado,
        cep: obra.cep,
        observacoes: formObs,
      });
      setEditandoObs(false);
      carregarObra();
      toast.success('Observações salvas!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao salvar observações.', { duration: 6000 });
    }
  }

  async function salvarPagamento() {
    const flags = getFormaFlags(formPagamento.forma_pagamento);
    if (!formPagamento.forma_pagamento) { toast.error('Selecione a forma de pagamento!'); return; }
    if (!formPagamento.valor_total || formPagamento.valor_total <= 0) { toast.error('Informe o valor para este método de pagamento!'); return; }
    if (flags.parcelado && !formPagamento.parcelas) { toast.error('Informe o número de parcelas!'); return; }
    if (flags.tem_dia_vencimento && !formPagamento.dia_vencimento) { toast.error('Informe o dia do mês para pagamento!'); return; }
    if (flags.tem_dia_vencimento && formPagamento.dia_vencimento) {
      const dia = parseInt(formPagamento.dia_vencimento);
      if (isNaN(dia) || dia < 1 || dia > 31) { toast.error('Dia de vencimento deve ser entre 1 e 31!'); return; }
    }
    if (!flags.tem_dia_vencimento && !formPagamento.prazo_pagamento) {
      toast.error('Informe o prazo de pagamento!'); return;
    }
    try {
      await api.post('/pagamentos', {
        obraid,
        forma_pagamento: formPagamento.forma_pagamento,
        prazo_pagamento: flags.tem_dia_vencimento ? undefined : formPagamento.prazo_pagamento,
        valor_total: formPagamento.valor_total,
        parcelas: formPagamento.parcelas,
        dia_vencimento: formPagamento.dia_vencimento,
        tem_dia_vencimento: flags.tem_dia_vencimento,
      });
      setFormPagamento({ forma_pagamento: '', prazo_pagamento: '', valor_total: '', valor_total_formatado: '', parcelas: '', dia_vencimento: '' });
      setMostrarFormPagamento(false);
      carregarPagamento();
      toast.success('Método de pagamento salvo!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao salvar pagamento.', { duration: 7000 });
    }
  }

  async function deletarMetodoPagamento(id) {
    try {
      await api.delete(`/pagamentos/${id}`);
      carregarPagamento();
      carregarHistorico();
      toast.success('Método removido!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Não foi possível remover este método.', { duration: 6000 });
    }
  }

  async function registrarRecebimento() {
    const multiplosMetodos = (pagamentoData?.pagamentos?.length || 0) > 1;
    if (!formRecebimento.data_recebimento || !formRecebimento.valor_recebido) {
      toast.error('Data e valor são obrigatórios!'); return;
    }
    if (multiplosMetodos && !formRecebimento.forma_pagamento) {
      toast.error('Informe o método de pagamento utilizado!'); return;
    }
    const pagamentoid = pagamentoData.pagamentos[0].pagamentoid;
    try {
      await api.post('/pagamentos/recebimento', {
        pagamentoid,
        data_recebimento: formRecebimento.data_recebimento,
        valor_recebido: formRecebimento.valor_recebido,
        observacao: formRecebimento.observacao,
        forma_pagamento: formRecebimento.forma_pagamento || pagamentoData.pagamentos[0].forma_pagamento,
      });
      setFormRecebimento({ data_recebimento: '', valor_recebido: '', valor_recebido_formatado: '', observacao: '', forma_pagamento: '' });
      setMostrarFormRecebimento(false);
      carregarPagamento();
      toast.success('Recebimento registrado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao registrar recebimento.', { duration: 7000 });
    }
  }

  function iniciarEdicaoPagamento(p) {
    setEditandoPagamento({
      pagamentoid: p.pagamentoid,
      forma_pagamento: p.forma_pagamento,
      valor_total: parseFloat(p.valor_total || 0),
      valor_total_formatado: parseFloat(p.valor_total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      parcelas: p.parcelas || '',
      dia_vencimento: p.dia_vencimento || '',
      prazo_pagamento: p.prazo_pagamento ? String(p.prazo_pagamento).split('T')[0] : '',
      justificativa: '',
    });
  }

  function handleValorEdicaoPagamento(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setEditandoPagamento({ ...editandoPagamento, valor_total: numero || 0, valor_total_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  async function salvarEdicaoPagamento() {
    if (!editandoPagamento.valor_total || editandoPagamento.valor_total <= 0) { toast.error('Informe o valor!'); return; }
    const flags = getFormaFlags(editandoPagamento.forma_pagamento);
    if (flags.parcelado && !editandoPagamento.parcelas) { toast.error('Informe o número de parcelas!'); return; }
    if (flags.tem_dia_vencimento && !editandoPagamento.dia_vencimento) { toast.error('Informe o dia do mês!'); return; }
    if (!flags.tem_dia_vencimento && !editandoPagamento.prazo_pagamento) { toast.error('Informe o prazo de pagamento!'); return; }
    try {
      await api.put(`/pagamentos/${editandoPagamento.pagamentoid}`, {
        forma_pagamento: editandoPagamento.forma_pagamento,
        valor_total: editandoPagamento.valor_total,
        parcelas: editandoPagamento.parcelas,
        dia_vencimento: editandoPagamento.dia_vencimento,
        prazo_pagamento: flags.tem_dia_vencimento ? undefined : editandoPagamento.prazo_pagamento,
        tem_dia_vencimento: flags.tem_dia_vencimento,
        justificativa: editandoPagamento.justificativa,
      });
      setEditandoPagamento(null);
      carregarPagamento();
      carregarHistorico();
      toast.success('Pagamento atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao atualizar pagamento.', { duration: 7000 });
    }
  }

  async function deletarRecebimento(id) {
    await api.delete(`/pagamentos/recebimento/${id}`);
    carregarPagamento();
    carregarHistorico();
    toast.success('Recebimento removido!');
  }

  function iniciarEdicaoRecebimento(r) {
    setEditandoRecebimento({
      recebimentoid: r.recebimentoid,
      data_recebimento: String(r.data_recebimento).split('T')[0],
      valor_recebido: parseFloat(r.valor_recebido || 0),
      valor_recebido_formatado: parseFloat(r.valor_recebido || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      observacao: r.observacao || '',
    });
  }

  function handleValorEdicaoRecebimento(e) {
    const raw = e.target.value.replace(/\D/g, '');
    const numero = parseFloat(raw) / 100;
    setEditandoRecebimento({ ...editandoRecebimento, valor_recebido: numero || 0, valor_recebido_formatado: raw ? numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' });
  }

  async function salvarEdicaoRecebimento() {
    if (!editandoRecebimento.data_recebimento || !editandoRecebimento.valor_recebido) {
      toast.error('Data e valor são obrigatórios!'); return;
    }
    try {
      await api.put(`/pagamentos/recebimento/${editandoRecebimento.recebimentoid}`, {
        data_recebimento: editandoRecebimento.data_recebimento,
        valor_recebido: editandoRecebimento.valor_recebido,
        observacao: editandoRecebimento.observacao,
      });
      setEditandoRecebimento(null);
      carregarPagamento();
      carregarHistorico();
      toast.success('Recebimento atualizado!');
    } catch (err) {
      toast.error(err.response?.data?.mensagem || 'Erro ao atualizar recebimento.', { duration: 6000 });
    }
  }

  function calcularProgressoMetodo(forma_pagamento, valor_total) {
    if (!pagamentoData?.recebimentos || !valor_total) return 0;
    const recebido = pagamentoData.recebimentos
      .filter((r) => r.forma_pagamento === forma_pagamento)
      .reduce((acc, r) => acc + parseFloat(r.valor_recebido), 0);
    return Math.min((recebido / parseFloat(valor_total)) * 100, 100).toFixed(1);
  }

  function calcularProgressoGeral() {
    if (!obra?.valor_fechado_obra || !pagamentoData?.total_recebido) return 0;
    return Math.min((pagamentoData.total_recebido / parseFloat(obra.valor_fechado_obra)) * 100, 100).toFixed(1);
  }

  if (!obra) return <div>Carregando...</div>;

  const statusAtual = obra.status_calculado || obra.status_obra;
  const obraConcluida = ['concluida', 'finalizada', 'concluida_pgto_pendente'].includes(statusAtual);
  const emAndamento = obra.status_obra !== 'orcamento';
  const totalOrcado = obra.total_orcado || 0;
  const totalMateriais = materiaisData.total_materiais || 0;
  const valorOrcado = totalOrcado + totalMateriais;
  const totalCompra = materiaisData.materiais.reduce((acc, m) => {
    const v = parseFloat(compraLocal[m.obra_material_id]?.valor_compra || 0);
    return acc + (isNaN(v) ? 0 : v);
  }, 0);
  const progressoGeral = calcularProgressoGeral();
  const hoje = new Date().toISOString().split('T')[0];
  const multiplosMetodos = (pagamentoData?.pagamentos?.length || 0) > 1;
  const formaAtual = getFormaFlags(formPagamento.forma_pagamento);

  const indiceAtual = listaObras.findIndex(o => o.id === obraid);
  const anterior = indiceAtual > 0 ? listaObras[indiceAtual - 1] : null;
  const proximo = indiceAtual >= 0 && indiceAtual < listaObras.length - 1 ? listaObras[indiceAtual + 1] : null;

  const COLUNAS_PDF_MATERIAIS = [
    { key: 'unidade',        label: 'Un.',         soObra: false, financeiro: false },
    { key: 'quantidade',     label: 'Qtd.',        soObra: false, financeiro: false },
    { key: 'preco_unitario', label: 'Preço Unit.', soObra: false, financeiro: true  },
    { key: 'total',          label: 'Total',       soObra: false, financeiro: true  },
    { key: 'fornecedor',     label: 'Fornecedor',  soObra: true,  financeiro: false },
    { key: 'valor_compra',   label: 'Vlr. Compra', soObra: true,  financeiro: true  },
    { key: 'data_compra',    label: 'Dt. Compra',  soObra: true,  financeiro: false },
    { key: 'data_entrega',   label: 'Dt. Entrega', soObra: true,  financeiro: false },
  ];

  return (
    <div>
      {/* modal PDF materiais */}
      {modalPdfMateriais.aberto && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--bg-modal)', borderRadius: 12, padding: 24, maxWidth: 400, width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 4px', color: 'var(--text-primary)' }}>PDF de Materiais</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>Selecione as colunas a incluir no relatório:</p>

            {/* Colunas fixas */}
            <div style={{ background: 'var(--bg-page)', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Sempre incluídas</div>
              <div style={{ display: 'flex', gap: 16 }}>
                {['Código', 'Descrição'].map(l => (
                  <span key={l} style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ color: '#059669', fontSize: 15 }}>✓</span> {l}
                  </span>
                ))}
              </div>
            </div>

            {/* Colunas opcionais */}
            <div style={{ background: 'var(--bg-page)', borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Opcionais</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', rowGap: 10, columnGap: 12 }}>
                {COLUNAS_PDF_MATERIAIS.map(col => {
                  const disabled = (col.soObra && obra.status_obra === 'orcamento') || (col.financeiro && !podeVerFinanceiro);
                  return (
                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1, color: 'var(--text-primary)' }}>
                      <input type="checkbox"
                        checked={modalPdfMateriais.colunas[col.key]}
                        disabled={disabled}
                        onChange={e => setModalPdfMateriais(m => ({ ...m, colunas: { ...m.colunas, [col.key]: e.target.checked } }))}
                        style={{ width: 15, height: 15, flexShrink: 0 }}
                      />
                      <span>{col.label}{disabled && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>🔒</span>}</span>
                    </label>
                  );
                })}
              </div>
              {obra.status_obra === 'orcamento' && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '10px 0 0' }}>🔒 Disponíveis após iniciar a obra</p>
              )}
              {!podeVerFinanceiro && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '10px 0 0' }}>🔒 Colunas financeiras bloqueadas por permissão</p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setModalPdfMateriais(m => ({ ...m, aberto: false }))}>Cancelar</button>
              <button className="btn-primary" onClick={() => { exportarMateriais(obra, materiaisData, modalPdfMateriais.colunas, configPDF); setModalPdfMateriais(m => ({ ...m, aberto: false })); }}>Gerar PDF</button>
            </div>
          </div>
        </div>
      )}

      {/* cabeçalho */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button className="btn-secondary" onClick={onVoltar}>← Voltar</button>

        {listaObras.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/obras/${anterior.id}`)}
              disabled={!anterior}
              title={anterior ? `← ${anterior.nome}` : ''}
              style={{ padding: '8px 12px', opacity: anterior ? 1 : 0.35 }}
            >
              ‹
            </button>
            <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 60, textAlign: 'center' }}>
              {indiceAtual + 1} / {listaObras.length}
            </span>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/obras/${proximo.id}`)}
              disabled={!proximo}
              title={proximo ? `${proximo.nome} →` : ''}
              style={{ padding: '8px 12px', opacity: proximo ? 1 : 0.35 }}
            >
              ›
            </button>
          </div>
        )}

        <h2 style={{ margin: 0, flex: 1 }}>🏗️ {obra.nome_obra}</h2>
        <div style={{ position: 'relative' }} ref={menuPdfRef}>
          <button className="btn-secondary" onClick={() => setMenuPdfAberto(v => !v)}>
            📄 Exportar PDF ▾
          </button>
          {menuPdfAberto && (
            <div style={{
              position: 'absolute', right: 0, top: '110%', background: '#fff',
              border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
              zIndex: 100, minWidth: 220, overflow: 'hidden',
            }}>
              {[
                obra.status_obra === 'orcamento' && { label: '📝 PDF Orçamento', sub: 'Proposta sem datas, para enviar ao cliente', fn: () => { exportarOrcamento(obra, materiaisData, configPDF); setMenuPdfAberto(false); } },
                { label: '📋 Cronograma', sub: 'Serviços e materiais, sem valores', fn: () => { exportarCronograma(obra, materiaisData, configPDF); setMenuPdfAberto(false); } },
                podeVerFinanceiro && { label: '🛠️ Relatório de Serviços e Materiais', sub: 'Serviços e materiais da obra, apenas valores totais', fn: () => { exportarServicosMateriais(obra, materiaisData, configPDF); setMenuPdfAberto(false); } },
                podeVerFinanceiro && { label: '📄 Relatório Completo', sub: 'Serviços, materiais e pagamentos', fn: () => { exportarRelatorioCompleto(obra, materiaisData, pagamentoData, configPDF); setMenuPdfAberto(false); } },
                podeVerFinanceiro && { label: '💰 Relatório Financeiro', sub: 'Valores, recebimentos e saldos', fn: () => { exportarRelatorioFinanceiro(obra, pagamentoData, configPDF); setMenuPdfAberto(false); } },
                materiaisData.materiais.length > 0 && { label: '🧱 PDF Materiais', sub: 'Lista de materiais com colunas a escolher', fn: () => { setModalPdfMateriais(m => ({ ...m, aberto: true })); setMenuPdfAberto(false); } },
              ].filter(Boolean).map((item, i, arr) => (
                <button key={i} onClick={item.fn} style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                  background: 'none', border: 'none', cursor: 'pointer', borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{item.sub}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* alertas de pagamento */}
      {pagamentoData?.alertas?.length > 0 && (
        <div style={{ background: 'rgba(251,191,36,0.13)', border: '1px solid #f59e0b', borderRadius: 10, padding: '14px 18px', marginBottom: 24 }}>
          <strong style={{ color: '#d97706' }}>⚠️ Atenção, pagamentos em atraso:</strong>
          {pagamentoData.alertas.map((a, i) => (
            <p key={i} style={{ marginTop: 6, fontSize: 14, color: 'var(--text-primary)' }}>
              • <strong>{a.forma_pagamento}</strong>, prazo era {formatarDataLocal(a.prazo_pagamento)}.
              Valor pendente: <strong>{formatarMoeda(a.valor_pendente)}</strong>
            </p>
          ))}
        </div>
      )}

      {/* informações gerais */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Informações Gerais</h3>
          {!editandoObra && obra.status_obra !== 'orcamento' && (
            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={iniciarEdicaoObra}>✏️ Editar</button>
          )}
        </div>

        {editandoObra ? (
          <div style={{ padding: 16, background: 'var(--cinza-claro)', borderRadius: 8, marginBottom: 12 }}>
            <input
              placeholder="Nome da obra"
              value={formEditObra.nome_obra}
              onChange={e => setFormEditObra({ ...formEditObra, nome_obra: e.target.value })}
            />
            <input
              placeholder="Valor fechado (Ex: R$ 10.000,00)"
              value={formEditObra.valor_fechado_obra_formatado}
              onChange={handleValorFechadoEdicao}
            />
            <textarea
              placeholder="Justificativa da alteração (opcional)"
              value={formEditObra.justificativa}
              onChange={e => setFormEditObra({ ...formEditObra, justificativa: e.target.value })}
              rows={2} style={{ resize: 'vertical' }}
            />
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4, marginTop: 4 }}>Endereço (opcional)</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                placeholder="CEP (Ex: 87013-060)"
                value={formEditObra.cep}
                onChange={e => setFormEditObra({ ...formEditObra, cep: formatarCep(e.target.value) })}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <button className="btn-secondary" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}
                onClick={() => buscarCep(formEditObra.cep, setFormEditObra)}>
                🔍 Buscar CEP
              </button>
            </div>
            <input
              placeholder="Logradouro (rua e número)"
              value={formEditObra.logradouro}
              onChange={e => setFormEditObra({ ...formEditObra, logradouro: e.target.value })}
            />
            <input
              placeholder="Bairro"
              value={formEditObra.bairro}
              onChange={e => setFormEditObra({ ...formEditObra, bairro: e.target.value })}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder="Cidade"
                value={formEditObra.cidade}
                onChange={e => setFormEditObra({ ...formEditObra, cidade: e.target.value })}
                style={{ flex: 2 }}
              />
              <input
                placeholder="UF"
                value={formEditObra.estado}
                onChange={e => setFormEditObra({ ...formEditObra, estado: e.target.value.toUpperCase().slice(0, 2) })}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={salvarEdicaoObra}>Salvar</button>
              <button className="btn-secondary" onClick={() => setEditandoObra(false)}>Cancelar</button>
            </div>
          </div>
        ) : (
          <table>
            <tbody>
              <tr><td><strong>Cliente</strong></td><td>{obra.nome_cliente}</td></tr>
              <tr>
                <td><strong>Telefone</strong></td>
                <td>
                  {(() => {
                    const tels = obra.telefones || [];
                    const tel = tels.find(t => t.principal) || tels[0];
                    if (!tel) return '—';
                    const icone = { celular: '📱', comercial: '🏢', fixo: '☎️' }[tel.tipo] || '📞';
                    return <a href={`tel:${tel.numero.replace(/\D/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>{icone} {tel.numero}</a>;
                  })()}
                </td>
              </tr>
              <tr>
                <td><strong>Email</strong></td>
                <td>
                  {obra.email_cliente
                    ? <a href={`mailto:${obra.email_cliente}`} style={{ color: 'inherit', textDecoration: 'none' }}>✉️ {obra.email_cliente}</a>
                    : '—'}
                </td>
              </tr>
              <tr><td><strong>Status</strong></td><td>{labelStatus(statusAtual)}</td></tr>
              {podeVerFinanceiro && <tr><td><strong>Total do(s) Serviço(s)</strong></td><td>{formatarMoeda(totalOrcado)}</td></tr>}
              {podeVerFinanceiro && <tr><td><strong>Total de Materiais</strong></td><td>{formatarMoeda(totalMateriais)}</td></tr>}
              {podeVerFinanceiro && <tr><td><strong>Valor Orçado</strong></td><td><strong>{formatarMoeda(valorOrcado)}</strong></td></tr>}
              {podeVerFinanceiro && <tr><td><strong>Valor Fechado</strong></td><td>{formatarMoeda(obra.valor_fechado_obra || 0)}</td></tr>}
              <tr>
                <td><strong>Prazo Combinado</strong></td>
                <td>{formatarDataLocal(obra.prazo_combinado)}</td>
              </tr>
              <tr>
                <td><strong>Prazo Entregue</strong></td>
                <td>{formatarDataLocal(obra.prazo_entregue)}</td>
              </tr>
              <tr>
                <td><strong>Endereço</strong></td>
                <td>
                  {obra.logradouro || obra.cidade ? (
                    <span>
                      {[obra.logradouro, obra.bairro].filter(Boolean).join(', ')}
                      {obra.cidade && (obra.logradouro || obra.bairro) ? ' — ' : ''}
                      {obra.cidade}{obra.estado ? `/${obra.estado}` : ''}
                      {obra.cep ? ` · ${obra.cep}` : ''}
                    </span>
                  ) : '—'}
                </td>
              </tr>
            </tbody>
          </table>
        )}

        {!apenasVerCronograma && (
          <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
            {obra.status_obra === 'orcamento' && (
              <button className="btn-primary" onClick={() => setMostrarFormIniciar(!mostrarFormIniciar)}>🚀 Iniciar Obra</button>
            )}
            {obra.status_obra === 'em_andamento' && (
              <>
                <button className="btn-primary" onClick={pedirConfirmacaoConcluir}>✅ Concluir Obra</button>
                <button className="btn-secondary" onClick={() => setMostrarFormPrazo(!mostrarFormPrazo)}>📅 Alterar Prazo</button>
                <button className="btn-secondary" onClick={() => { setMostrarFormPausar(!mostrarFormPausar); setMostrarFormCancelar(false); }}>⏸️ Pausar</button>
              </>
            )}
            {obraConcluida && (
              <button className="btn-secondary" onClick={() => setMostrarFormReabrir(!mostrarFormReabrir)}>🔄 Reabrir Obra</button>
            )}
            {['em_andamento', 'pausada'].includes(obra.status_obra) && (
              <button className="btn-danger" onClick={() => { setMostrarFormCancelar(!mostrarFormCancelar); setMostrarFormPausar(false); }}>🚫 Cancelar Obra</button>
            )}
            {obra.status_obra === 'orcamento' && (
              <button className="btn-danger" onClick={() => setMostrarFormRecusar(!mostrarFormRecusar)}>❌ Recusar Orçamento</button>
            )}
            {['cancelada', 'pausada', 'orcamento_recusado'].includes(obra.status_obra) && (
              <button className="btn-secondary" onClick={() => setMostrarFormReativar(!mostrarFormReativar)}>▶️ Reativar</button>
            )}
          </div>
        )}

        {mostrarFormIniciar && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
            <h3>Iniciar Obra</h3>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Prazo combinado *</label>
            <input type="date" min={hoje} value={formIniciar.prazo_combinado}
              onChange={(e) => setFormIniciar({ ...formIniciar, prazo_combinado: e.target.value })} />
            <input placeholder="Valor fechado * (Ex: R$ 10.000,00)" value={formIniciar.valor_fechado_obra_formatado} onChange={handleValorFechado} />
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Endereço da obra (opcional)</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                placeholder="CEP (Ex: 87013-060)"
                value={formIniciar.cep}
                onChange={e => setFormIniciar({ ...formIniciar, cep: formatarCep(e.target.value) })}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <button className="btn-secondary" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}
                onClick={() => buscarCep(formIniciar.cep, setFormIniciar)}>
                🔍 Buscar CEP
              </button>
            </div>
            <input
              placeholder="Logradouro (rua e número)"
              value={formIniciar.logradouro}
              onChange={e => setFormIniciar({ ...formIniciar, logradouro: e.target.value })}
            />
            <input
              placeholder="Bairro"
              value={formIniciar.bairro}
              onChange={e => setFormIniciar({ ...formIniciar, bairro: e.target.value })}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                placeholder="Cidade"
                value={formIniciar.cidade}
                onChange={e => setFormIniciar({ ...formIniciar, cidade: e.target.value })}
                style={{ flex: 2 }}
              />
              <input
                placeholder="UF"
                value={formIniciar.estado}
                onChange={e => setFormIniciar({ ...formIniciar, estado: e.target.value.toUpperCase().slice(0, 2) })}
                style={{ flex: 1 }}
              />
            </div>
            <button className="btn-primary" onClick={iniciarObra}>Confirmar</button>
          </div>
        )}

        {mostrarFormPrazo && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
            <h3>Alterar Prazo</h3>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Novo prazo *</label>
            <input type="date" min={hoje} value={formPrazo.novo_prazo}
              onChange={(e) => setFormPrazo({ ...formPrazo, novo_prazo: e.target.value })} />
            <textarea placeholder="Justificativa (opcional)" value={formPrazo.justificativa}
              onChange={(e) => setFormPrazo({ ...formPrazo, justificativa: e.target.value })}
              rows={3} style={{ resize: 'vertical' }} />
            <button className="btn-primary" onClick={alterarPrazo}>Salvar</button>
          </div>
        )}

        {mostrarFormReabrir && (
          <div style={{ marginTop: 16, padding: 16, background: 'rgba(251,191,36,0.13)', borderRadius: 8, border: '1px solid #f59e0b' }}>
            <h3>Reabrir Obra</h3>
            <textarea
              placeholder="Justificativa (opcional)"
              value={justificativaReabrir}
              onChange={(e) => setJustificativaReabrir(e.target.value)}
              rows={3} style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={() => reabrirObra(false)}>Confirmar Reabertura</button>
              <button className="btn-secondary" onClick={() => { setMostrarFormReabrir(false); setJustificativaReabrir(''); }}>Cancelar</button>
            </div>
          </div>
        )}

        {mostrarFormPausar && (
          <div style={{ marginTop: 16, padding: 16, background: '#e8e0f8', borderRadius: 8, border: '1px solid #a78bfa' }}>
            <h3>⏸️ Pausar Obra</h3>
            <textarea
              placeholder="Justificativa obrigatória: motivo da pausa *"
              value={justificativaPausar}
              onChange={(e) => setJustificativaPausar(e.target.value)}
              rows={3} style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={pausarObra}>Confirmar Pausa</button>
              <button className="btn-secondary" onClick={() => { setMostrarFormPausar(false); setJustificativaPausar(''); }}>Cancelar</button>
            </div>
          </div>
        )}

        {mostrarFormCancelar && (
          <div style={{ marginTop: 16, padding: 16, background: '#f8d7da', borderRadius: 8, border: '1px solid #f5c2c7' }}>
            <h3 style={{ color: '#842029' }}>🚫 Cancelar Obra</h3>
            <p style={{ fontSize: 13, color: '#842029', marginBottom: 12 }}>⚠️ A obra será marcada como cancelada. Você poderá reativá-la depois se necessário.</p>
            <textarea
              placeholder="Justificativa obrigatória: motivo do cancelamento *"
              value={justificativaCancelar}
              onChange={(e) => setJustificativaCancelar(e.target.value)}
              rows={3} style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-danger" onClick={cancelarObra}>Confirmar Cancelamento</button>
              <button className="btn-secondary" onClick={() => { setMostrarFormCancelar(false); setJustificativaCancelar(''); }}>Cancelar</button>
            </div>
          </div>
        )}

        {mostrarFormReativar && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
            <h3>▶️ Reativar Obra</h3>
            <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>A obra será reativada e o evento registrado no histórico.</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-primary" onClick={() => reativarObra(false)}>Confirmar Reativação</button>
              <button className="btn-secondary" onClick={() => setMostrarFormReativar(false)}>Cancelar</button>
            </div>
          </div>
        )}

        {mostrarFormRecusar && (
          <div style={{ marginTop: 16, padding: 16, background: '#fce7f3', borderRadius: 8, border: '1px solid #fbcfe8' }}>
            <h3>❌ Recusar Orçamento</h3>
            <p style={{ fontSize: 13, color: '#9d174d', marginBottom: 12 }}>⚠️ O orçamento será marcado como recusado. Você poderá reativá-lo depois se necessário.</p>
            <textarea
              placeholder="Motivo (opcional)"
              value={justificativaRecusar}
              onChange={(e) => setJustificativaRecusar(e.target.value)}
              rows={3} style={{ resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-danger" onClick={recusarOrcamento}>Confirmar Recusa</button>
              <button className="btn-secondary" onClick={() => { setMostrarFormRecusar(false); setJustificativaRecusar(''); }}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* observações internas + projetos */}
      <div className="card">
        {/* Abas */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#f3f4f6', borderRadius: 8, padding: 4, width: 'fit-content' }}>
          {[
            { id: 'obs', label: '📝 Observações' },
            ...(temPermissao('projetos_cad_ver') || temPermissao('projetos_doc_ver') ? [{ id: 'projetos', label: '📐 Projetos' }] : []),
          ].map(aba => (
            <button
              key={aba.id}
              onClick={() => setAbaObs(aba.id)}
              style={{
                padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
                background: abaObs === aba.id ? '#fff' : 'transparent',
                color: abaObs === aba.id ? 'var(--azul-medio)' : '#6b7280',
                boxShadow: abaObs === aba.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              }}
            >
              {aba.label}
            </button>
          ))}
        </div>

        {/* Conteúdo: Observações */}
        {abaObs === 'obs' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              {!editandoObs && (
                <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => { setFormObs(obra.observacoes || ''); setEditandoObs(true); }}>
                  ✏️ Editar
                </button>
              )}
            </div>
            {editandoObs ? (
              <div>
                <textarea
                  value={formObs}
                  onChange={e => setFormObs(e.target.value.slice(0, 3000))}
                  rows={6}
                  style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
                  placeholder={`Uso interno da equipe, não aparece em relatórios externos.\n\nExemplos:\n• Acesso ao canteiro: portão lateral, chave com o Sr. João (zelador)\n• Encarregado: Carlos Ferreira — (44) 98888-1234\n• Cliente prefere contato por WhatsApp, não ligar antes das 9h\n• Atenção: laje do 2º pavimento com reforço especial, verificar projeto estrutural`}
                  autoFocus
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                  <span style={{ fontSize: 12, color: formObs.length > 2800 ? '#dc2626' : '#999' }}>
                    {formObs.length}/3000 caracteres
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-secondary" onClick={() => setEditandoObs(false)}>Cancelar</button>
                    <button className="btn-primary" onClick={salvarObservacoes}>Salvar</button>
                  </div>
                </div>
              </div>
            ) : (
              obra.observacoes ? (
                <p style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#374151', margin: 0, lineHeight: 1.7 }}>
                  {obra.observacoes}
                </p>
              ) : (
                <p style={{ color: '#9ca3af', fontSize: 13, margin: 0, fontStyle: 'italic' }}>
                  Nenhuma observação registrada. Use este espaço para informações internas da equipe: acesso ao canteiro, nome do encarregado, particularidades do cliente, alertas técnicos, etc.
                </p>
              )
            )}
          </>
        )}

        {/* Conteúdo: Projetos */}
        {abaObs === 'projetos' && (temPermissao('projetos_cad_ver') || temPermissao('projetos_doc_ver')) && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              {(temPermissao('projetos_cad_anexar') || temPermissao('projetos_doc_anexar')) && (
                <>
                  <button
                    className="btn-primary"
                    style={{ fontSize: 13, padding: '6px 14px' }}
                    onClick={() => inputProjetoRef.current?.click()}
                    disabled={uploadandoProjeto}
                  >
                    {uploadandoProjeto ? '⏳ Enviando...' : '+ Anexar arquivo'}
                  </button>
                  <input
                    ref={inputProjetoRef}
                    type="file"
                    accept={[
                      temPermissao('projetos_cad_anexar') ? '.dwg,.dxf' : '',
                      temPermissao('projetos_doc_anexar') ? '.pdf,.png,.jpg,.jpeg' : '',
                    ].filter(Boolean).join(',')}
                    style={{ display: 'none' }}
                    onChange={handleUploadProjeto}
                  />
                </>
              )}
            </div>

            {projetos.length === 0 ? (
              <p style={{ color: '#9ca3af', fontSize: 13, fontStyle: 'italic', textAlign: 'center', padding: '24px 0' }}>
                Nenhum arquivo anexado ainda.
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {projetos.map(p => {
                  const isImagem = ['png', 'jpg', 'jpeg'].includes(p.extensao);
                  const isPdf = p.extensao === 'pdf';
                  const isCad = ['dwg', 'dxf'].includes(p.extensao);
                  const urlArquivo = `${api.defaults.baseURL}/obra-projetos/arquivo/${p.projetoid}`;

                  return (
                    <div key={p.projetoid} style={{
                      border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                      background: '#fafafa', display: 'flex', flexDirection: 'column',
                    }}>
                      <div
                        style={{
                          height: 130, background: '#f3f4f6', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          cursor: isImagem || isPdf ? 'pointer' : 'default',
                          overflow: 'hidden',
                        }}
                        onClick={() => (isImagem || isPdf) && setPreviewProjeto(p)}
                        title={isImagem || isPdf ? 'Clique para ampliar' : ''}
                      >
                        {isImagem || isPdf ? (
                          <ArquivoPreview
                            projetoid={p.projetoid}
                            extensao={p.extensao}
                            nomeOriginal={p.nome_original}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onClick={() => setPreviewProjeto(p)}
                          />
                        ) : (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 38 }}>{ICONE_EXT[p.extensao] || '📁'}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4, textTransform: 'uppercase', fontWeight: 700 }}>{p.extensao}</div>
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '8px 10px', flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nome_original}>
                          {p.nome_original}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                          {formatarTamanho(p.tamanho)} · {formatarDataLocal(p.criado_em)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', borderTop: '1px solid #e5e7eb' }}>
                        <button
                          onClick={() => abrirArquivo(p)}
                          style={{
                            flex: 1, padding: '7px', border: 'none', background: 'none', cursor: 'pointer',
                            textAlign: 'center', fontSize: 12, color: 'var(--azul-medio)', fontWeight: 600,
                            borderRight: temPermissao(permDeletarProjeto(p.extensao)) ? '1px solid #e5e7eb' : 'none',
                          }}
                        >
                          {isCad ? '⬇ Baixar' : '🔍 Abrir'}
                        </button>
                        {temPermissao(permDeletarProjeto(p.extensao)) && (
                          <button
                            onClick={() => setConfirmDeleteProjeto(p)}
                            style={{ flex: 1, padding: '7px', border: 'none', background: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          >
                            🗑 Remover
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* materiais */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>🧱 Materiais</h3>
          {!apenasVerCronograma && (
            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => { setMostrarFormMaterialManual(!mostrarFormMaterialManual); setItemSelecionado(null); setComposicaoAnalitica(null); }}>
              + Item manual
            </button>
          )}
        </div>

        {/* busca SINAPI */}
        {!apenasVerCronograma && <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <input
              placeholder="Buscar no SINAPI por código ou descrição..."
              value={buscaSinapi}
              onChange={e => { setBuscaSinapi(e.target.value); buscarSinapiItems(e.target.value); }}
              style={{ flex: 1, minWidth: 200, marginBottom: 0 }}
            />
            <select
              value={ufBusca}
              onChange={e => { setUfBusca(e.target.value); if (buscaSinapi.length >= 2) buscarSinapiItems(buscaSinapi, e.target.value); }}
              style={{ width: 80, marginBottom: 0 }}
            >
              <option value="">UF</option>
              {ufsDisponiveis.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            <select value={formMaterial.tipo_encargo} onChange={e => { setFormMaterial(f => ({ ...f, tipo_encargo: e.target.value })); setResultadosBusca([]); setComposicaoAnalitica(null); }} style={{ width: 160, marginBottom: 0 }}>
              <option value="sem_desone">SD</option>
              <option value="com_desone">CD</option>
              <option value="sem_encargos">SE</option>
              {temAnalitico && <option value="analitico">Analítico</option>}
            </select>
          </div>
          {formMaterial.tipo_encargo === 'analitico' && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Busque uma composição e escolha quais insumos/sub-composições dela lançar na obra.
            </p>
          )}
          {!ufBusca && <p style={{ fontSize: 12, color: '#dc2626', margin: '4px 0 0' }}>Selecione um estado para buscar.</p>}
          {buscando && <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Buscando...</p>}
          {resultadosBusca.length > 0 && (
            <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, maxHeight: 260, overflowY: 'auto', background: 'var(--bg-card)' }}>
              {resultadosBusca.map(item => (
                <div
                  key={`${item.tipo}-${item.id}`}
                  onClick={() => selecionarItemSinapi(item)}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', fontSize: 13, background: 'var(--bg-card)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--tr-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ color: 'var(--text-primary)' }}><strong>{item.codigo}</strong> · {item.descricao}</span>
                    <span style={{ whiteSpace: 'nowrap', color: '#059669', fontWeight: 600 }}>
                      {item[`preco_${formMaterial.tipo_encargo}`]
                        ? parseFloat(item[`preco_${formMaterial.tipo_encargo}`]).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                        : '—'} / {item.unidade}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {item.tipo === 'composicao' ? 'Composição' : 'Insumo'} · {item.classificacao}
                    {item.uf ? ` · ${item.uf}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>}

        {/* itens analíticos da composição selecionada */}
        {!apenasVerCronograma && composicaoAnalitica && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>{composicaoAnalitica.codigo} · {composicaoAnalitica.descricao}</strong>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Composição analítica ({ufBusca})</div>
              </div>
              <button className="btn-secondary" style={{ fontSize: 11 }} onClick={() => { setComposicaoAnalitica(null); setItensAnalitico([]); }}>✕</button>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <input placeholder={`Quantidade de ${composicaoAnalitica.unidade} *`} type="number" min="0.01" step="0.01"
                value={qtdComposicaoAnalitica}
                onChange={e => setQtdComposicaoAnalitica(e.target.value)}
                style={{ width: 180, marginBottom: 0 }}
              />
              <select
                value={encargoAnalitico}
                onChange={e => { setEncargoAnalitico(e.target.value); carregarItensAnalitico(composicaoAnalitica.codigo, ufBusca, e.target.value); }}
                style={{ width: 160, marginBottom: 0 }}
                title="Encargo usado para calcular o preço dos itens"
              >
                <option value="sem_desone">Preços SD</option>
                <option value="com_desone">Preços CD</option>
                <option value="sem_encargos">Preços SE</option>
              </select>
            </div>

            {carregandoAnalitico ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Carregando itens...</p>
            ) : itensAnalitico.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nenhum item encontrado para essa composição.</p>
            ) : (
              <>
                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}></th>
                        <th>Item</th>
                        <th>Unid.</th>
                        <th>Qtd.</th>
                        <th>Preço unit.</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itensAnalitico.map(it => {
                        const qtdCalculada = (it.coeficiente || 0) * (parseFloat(qtdComposicaoAnalitica) || 0);
                        return (
                          <tr key={it.item_codigo}>
                            <td>
                              <input type="checkbox"
                                checked={!!itensAnaliticoSelecionados[it.item_codigo]}
                                onChange={() => toggleItemAnalitico(it.item_codigo)}
                              />
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {it.item_codigo} · {it.descricao}
                              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{it.tipo_item === 'INSUMO' ? 'Insumo' : 'Composição'}</div>
                            </td>
                            <td style={{ fontSize: 12 }}>{it.unidade}</td>
                            <td style={{ fontSize: 12 }}>{qtdCalculada.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}</td>
                            <td style={{ fontSize: 12 }}>
                              {it.preco_unitario != null
                                ? it.preco_unitario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                : <span style={{ color: '#dc2626' }}>sem preço</span>}
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {it.custo_total != null
                                ? (it.custo_total * (parseFloat(qtdComposicaoAnalitica) || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button className="btn-primary" style={{ marginTop: 12 }} onClick={adicionarItensAnalitico}>
                  Adicionar itens selecionados
                </button>
              </>
            )}
          </div>
        )}

        {/* form item SINAPI selecionado */}
        {!apenasVerCronograma && itemSelecionado && (
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <strong style={{ fontSize: 13, color: 'var(--text-primary)' }}>{itemSelecionado.codigo} · {itemSelecionado.descricao}</strong>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{itemSelecionado.unidade} · {labelEncargo(formMaterial.tipo_encargo)}</div>
              </div>
              <button className="btn-secondary" style={{ fontSize: 11 }} onClick={() => setItemSelecionado(null)}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input placeholder="Quantidade *" type="number" min="0.01" step="0.01"
                value={formMaterial.quantidade}
                onChange={e => setFormMaterial(f => ({ ...f, quantidade: e.target.value }))}
                style={{ width: 130, marginBottom: 0 }}
              />
              <input placeholder="Preço unitário *"
                value={formMaterial.preco_formatado}
                onChange={handlePrecoBusca}
                style={{ width: 160, marginBottom: 0 }}
              />
              <input placeholder="Observação (opcional)"
                value={formMaterial.observacao}
                onChange={e => setFormMaterial(f => ({ ...f, observacao: e.target.value }))}
                style={{ flex: 1, minWidth: 160, marginBottom: 0 }}
              />
              <button className="btn-primary" onClick={adicionarMaterialSinapi} style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
                Adicionar
              </button>
            </div>
            {formMaterial.quantidade && formMaterial.preco_unitario > 0 && (
              <div style={{ fontSize: 13, marginTop: 8, color: 'var(--text-secondary)' }}>
                Total: <strong>{(parseFloat(formMaterial.quantidade) * formMaterial.preco_unitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</strong>
              </div>
            )}
          </div>
        )}

        {/* form manual */}
        {mostrarFormMaterialManual && (
          <div style={{ background: 'var(--cinza-claro)', borderRadius: 8, padding: 14, marginBottom: 16 }}>
            <h4 style={{ margin: '0 0 10px' }}>Item Manual (não SINAPI)</h4>
            <input placeholder="Descrição *" value={formManual.descricao}
              onChange={e => setFormManual(f => ({ ...f, descricao: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input placeholder="Unidade (ex: m², kg, un)" value={formManual.unidade}
                onChange={e => setFormManual(f => ({ ...f, unidade: e.target.value }))}
                style={{ width: 160, marginBottom: 0 }} />
              <input placeholder="Quantidade *" type="number" min="0.01" step="0.01"
                value={formManual.quantidade}
                onChange={e => setFormManual(f => ({ ...f, quantidade: e.target.value }))}
                style={{ width: 130, marginBottom: 0 }} />
              <input placeholder="Preço unitário"
                value={formManual.preco_formatado}
                onChange={handlePrecoManual}
                style={{ width: 160, marginBottom: 0 }} />
              <input placeholder="Observação" value={formManual.observacao}
                onChange={e => setFormManual(f => ({ ...f, observacao: e.target.value }))}
                style={{ flex: 1, minWidth: 160, marginBottom: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn-primary" onClick={adicionarMaterialManual}>Adicionar</button>
              <button className="btn-secondary" onClick={() => { setMostrarFormMaterialManual(false); setFormManual({ descricao: '', unidade: '', quantidade: '', preco_unitario: '', preco_formatado: '', observacao: '' }); }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* lista de materiais */}
        {materiaisData.materiais.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: emAndamento ? 1050 : 'unset' }}>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Un.</th>
                  <th>Qtd.</th>
                  <th>Preço Unit.</th>
                  <th>Total</th>
                  {emAndamento && <th style={{ width: 160 }}>Fornecedor</th>}
                  {emAndamento && podeVerFinanceiro && <th style={{ width: 110, textAlign: 'right' }}>Vlr. Compra</th>}
                  {emAndamento && <th style={{ width: 110 }}>Dt. Compra</th>}
                  {emAndamento && <th style={{ width: 110 }}>Dt. Entrega</th>}
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {materiaisData.materiais.map(m => (
                  <tr key={m.obra_material_id}>
                    {editandoMaterial?.obra_material_id === m.obra_material_id ? (
                      <>
                        <td>{m.codigo_sinapi || '—'}</td>
                        <td><input value={editandoMaterial.descricao} onChange={e => setEditandoMaterial(x => ({ ...x, descricao: e.target.value }))} style={{ marginBottom: 0 }} /></td>
                        <td><input value={editandoMaterial.unidade || ''} onChange={e => setEditandoMaterial(x => ({ ...x, unidade: e.target.value }))} style={{ width: 60, marginBottom: 0 }} /></td>
                        <td><input type="number" min="0.01" step="0.01" value={editandoMaterial.quantidade} onChange={e => setEditandoMaterial(x => ({ ...x, quantidade: e.target.value }))} style={{ width: 110, marginBottom: 0 }} /></td>
                        {podeVerFinanceiro ? <td><input value={editandoMaterial.preco_formatado} onChange={handlePrecoEdicaoMaterial} style={{ width: 120, marginBottom: 0 }} /></td> : <td>—</td>}
                        {podeVerFinanceiro ? <td>{(parseFloat(editandoMaterial.quantidade || 0) * (editandoMaterial.preco_unitario || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td> : <td>—</td>}
                        {emAndamento && <CompraInlineCells m={m} compraLocal={compraLocal} setCompraLocal={setCompraLocal} salvarCompra={salvarCompra} fornecedores={fornecedores} disabled={obraConcluida} podeVerFinanceiro={podeVerFinanceiro} />}
                        <td>
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button className="btn-primary" style={{ fontSize: 11, padding: '3px 8px' }} onClick={salvarEdicaoMaterial}>Salvar</button>
                            <button className="btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditandoMaterial(null)}>✕</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {m.codigo_sinapi || <em>manual</em>}
                          {m.tipo_encargo && <div style={{ fontSize: 10 }}>{labelEncargo(m.tipo_encargo)}{m.uf ? ` · ${m.uf}` : ''}</div>}
                        </td>
                        <td>
                          <div style={{ color: 'var(--text-primary)' }}>{m.descricao}</div>
                          {m.observacao && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>💬 {m.observacao}</div>}
                          {m.sinapi_mes && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>SINAPI {m.sinapi_mes}</div>}
                        </td>
                        <td>{m.unidade || '—'}</td>
                        <td>{parseFloat(m.quantidade).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}</td>
                        <td>{podeVerFinanceiro ? parseFloat(m.preco_unitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</td>
                        <td><strong>{podeVerFinanceiro ? parseFloat(m.total).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</strong></td>
                        {emAndamento && <CompraInlineCells m={m} compraLocal={compraLocal} setCompraLocal={setCompraLocal} salvarCompra={salvarCompra} fornecedores={fornecedores} disabled={obraConcluida} podeVerFinanceiro={podeVerFinanceiro} />}
                        <td>
                          {!apenasVerCronograma && (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="btn-secondary" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => setEditandoMaterial({ ...m, preco_formatado: parseFloat(m.preco_unitario).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) })}>✏️</button>
                              <button className="btn-danger" style={{ fontSize: 11, padding: '3px 8px' }} onClick={() => removerMaterial(m.obra_material_id)}>🗑️</button>
                            </div>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600, paddingRight: 12 }}>Total de Materiais:</td>
                  <td><strong style={{ color: '#059669' }}>{podeVerFinanceiro ? materiaisData.total_materiais.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}</strong></td>
                  {emAndamento && podeVerFinanceiro && (
                    <>
                      <td />
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Total Vlr. Compra:</span>
                          <strong style={{ color: '#2563eb' }}>
                            {totalCompra > 0 ? totalCompra.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—'}
                          </strong>
                        </div>
                      </td>
                      <td colSpan={2} />
                    </>
                  )}
                  {emAndamento && !podeVerFinanceiro && <td colSpan={3} />}
                  <td />
                </tr>
              </tfoot>
            </table>
            </div>
          </>
        ) : (
          <p style={{ color: '#9ca3af', fontSize: 13, fontStyle: 'italic', margin: 0 }}>
            Nenhum material adicionado ainda. Busque no SINAPI ou adicione um item manual.
          </p>
        )}
      </div>

      {/* serviços da obra */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h3>Serviços</h3>
            <div style={{ display: 'flex', background: 'var(--bg-page)', borderRadius: 8, padding: 3, gap: 2 }}>
              {[{ id: 'lista', label: '☰ Lista' }, { id: 'gantt', label: '📅 Cronograma' }].map(aba => (
                <button
                  key={aba.id}
                  onClick={() => setAbaServicos(aba.id)}
                  style={{
                    padding: '4px 12px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', borderRadius: 6,
                    background: abaServicos === aba.id ? 'var(--bg-card)' : 'transparent',
                    color: abaServicos === aba.id ? 'var(--azul-medio)' : 'var(--text-muted)',
                    boxShadow: abaServicos === aba.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {aba.label}
                </button>
              ))}
            </div>
          </div>
          {!obraConcluida && !apenasVerCronograma && abaServicos === 'lista' && (
            <button className="btn-secondary" onClick={() => setMostrarFormServico(!mostrarFormServico)}>
              {mostrarFormServico ? 'Cancelar' : '+ Adicionar Serviço'}
            </button>
          )}
        </div>

        {obraConcluida && (
          <p style={{ color: '#d97706', background: 'rgba(251,191,36,0.13)', border: '1px solid #f59e0b', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
            ⚠️ A obra está concluída. Reabra a obra para adicionar ou modificar serviços.
          </p>
        )}

        {mostrarFormServico && !obraConcluida && (
          <div style={{ marginBottom: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
            <select value={formServico.servicoid}
              onChange={(e) => setFormServico({ ...formServico, servicoid: e.target.value })}>
              <option value="">Selecione o tipo de serviço</option>
              {servicosDisponiveis.map((s) => (
                <option key={s.servicoid} value={s.servicoid}>{s.nome_servico}</option>
              ))}
            </select>
            <input placeholder="Descrição específica" value={formServico.descricao_servico_obra}
              onChange={(e) => setFormServico({ ...formServico, descricao_servico_obra: e.target.value })} />
            <input placeholder="Valor orçado (Ex: R$ 5.000,00)" value={formServico.valor_orcado_os_formatado} onChange={handleValorServico} />
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Data início</label>
                <input type="date" value={formServico.data_inicio} disabled={obra.status_obra === 'orcamento'}
                  onChange={(e) => setFormServico({ ...formServico, data_inicio: e.target.value })}
                  style={{ opacity: obra.status_obra === 'orcamento' ? 0.45 : 1, cursor: obra.status_obra === 'orcamento' ? 'not-allowed' : 'auto' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Data fim prevista</label>
                <input type="date" value={formServico.data_fim} disabled={obra.status_obra === 'orcamento'}
                  onChange={(e) => setFormServico({ ...formServico, data_fim: e.target.value })}
                  style={{ opacity: obra.status_obra === 'orcamento' ? 0.45 : 1, cursor: obra.status_obra === 'orcamento' ? 'not-allowed' : 'auto' }} />
              </div>
              {obra.status_obra === 'orcamento' && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  📅 Datas disponíveis após iniciar a obra.
                </p>
              )}
            </div>
            <select
              value={formServico.responsavel_id}
              onChange={(e) => setFormServico({ ...formServico, responsavel_id: e.target.value })}
            >
              <option value="">Responsável pelo serviço *</option>
              {responsaveis.map(r => (
                <option key={r.clienteid} value={r.clienteid}>{r.nome_cliente} ({r.tipo_cliente})</option>
              ))}
            </select>
            {obra.servicos?.length > 0 && (
              <div>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'block', marginBottom: 6, fontWeight: 500 }}>
                  Depende de (opcional)
                </label>
                <div style={{ border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden', maxHeight: 140, overflowY: 'auto' }}>
                  {obra.servicos.map((svc, idx) => (
                    <label key={svc.obraservicoid} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', cursor: 'pointer',
                      background: idx % 2 === 0 ? 'var(--bg-card)' : 'var(--cinza-claro)',
                      borderBottom: idx < obra.servicos.length - 1 ? '1px solid var(--border-color)' : 'none',
                      margin: 0,
                    }}>
                      <input
                        type="checkbox"
                        style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: '#3b82f6', margin: 0, display: 'block' }}
                        checked={formServico.predecessores.map(Number).includes(Number(svc.obraservicoid))}
                        onChange={e => {
                          const id = Number(svc.obraservicoid);
                          const novaLista = e.target.checked
                            ? [...formServico.predecessores, id]
                            : formServico.predecessores.filter(p => p !== id);
                          // Ajusta data_inicio automaticamente para o dia após o último predecessor
                          const maxFimStr = obra.servicos
                            .filter(s => novaLista.includes(Number(s.obraservicoid)) && s.data_fim)
                            .reduce((max, s) => {
                              const d = new Date(s.data_fim).toISOString().split('T')[0];
                              return d > max ? d : max;
                            }, '');
                          let novoInicio = formServico.data_inicio;
                          if (maxFimStr) {
                            const minStart = new Date(maxFimStr);
                            minStart.setDate(minStart.getDate() + 1);
                            const minStartStr = minStart.toISOString().split('T')[0];
                            if (!novoInicio || novoInicio <= maxFimStr) novoInicio = minStartStr;
                          }
                          setFormServico({ ...formServico, predecessores: novaLista, data_inicio: novoInicio });
                        }}
                      />
                      <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>
                        {svc.nome_servico}
                        {svc.descricao_servico_obra && (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>: {svc.descricao_servico_obra}</span>
                        )}
                      </span>
                      {svc.data_fim && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          até {String(svc.data_fim).split('T')[0].split('-').reverse().join('/')}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button className="btn-primary" style={{ marginTop: 8 }} onClick={adicionarServico}>Adicionar</button>
          </div>
        )}

        {abaServicos === 'gantt' ? (
          <GanttServicos servicos={obra.servicos} prazoCombinado={obra.prazo_combinado} />
        ) : (
        <>
        <table>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Descrição</th>
              <th>Responsável</th>
              <th>Valor Orçado</th>
              <th>Período</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {obra.servicos?.map((s) => {
              const hojeDate = new Date(); hojeDate.setHours(0, 0, 0, 0);
              const dataFim = s.data_fim ? new Date(s.data_fim) : null;
              const estaAtrasado = dataFim && dataFim < hojeDate && s.status_servico !== 'concluido';
              const diasEmAtraso = estaAtrasado && dataFim ? Math.round((hojeDate - dataFim) / (1000 * 60 * 60 * 24)) : 0;
              const hojeLocal = new Date();
              const hojeStr = `${hojeLocal.getFullYear()}-${String(hojeLocal.getMonth() + 1).padStart(2, '0')}-${String(hojeLocal.getDate()).padStart(2, '0')}`;
              const estaeditando = editandoServico?.obraservicoid === s.obraservicoid;

              return (
                <React.Fragment key={s.obraservicoid}>
                  <tr style={{ background: estaAtrasado ? 'rgba(239,68,68,0.08)' : estaeditando ? 'rgba(59,130,246,0.08)' : 'inherit' }}>
                    <td data-label="Tipo">{s.nome_servico}</td>
                    <td data-label="Descrição">
                      {estaeditando
                        ? <input value={editandoServico.descricao_servico_obra} onChange={e => setEditandoServico({ ...editandoServico, descricao_servico_obra: e.target.value })} style={{ marginBottom: 0 }} />
                        : s.descricao_servico_obra || '—'}
                    </td>
                    <td data-label="Responsável">
                      {estaeditando
                        ? (
                          <select
                            value={editandoServico.responsavel_id || ''}
                            onChange={e => setEditandoServico({ ...editandoServico, responsavel_id: e.target.value })}
                            style={{ marginBottom: 0 }}
                          >
                            <option value="">Selecionar *</option>
                            {responsaveis.map(r => (
                              <option key={r.clienteid} value={r.clienteid}>{r.nome_cliente} ({r.tipo_cliente})</option>
                            ))}
                          </select>
                        )
                        : (
                          <div>
                            <div>{s.responsavel_nome || '—'}</div>
                            {s.predecessores?.length > 0 && (
                              <div style={{ marginTop: 3, fontSize: 11, color: 'var(--text-muted)' }}>
                                🔗 Depende de: {s.predecessores.map(p => p.nome_servico).join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                    </td>
                    <td data-label="Valor Orçado">
                      {estaeditando
                        ? (podeVerFinanceiro ? <input value={editandoServico.valor_orcado_os_formatado} onChange={handleValorEdicaoServico} style={{ marginBottom: 0 }} /> : '—')
                        : (podeVerFinanceiro ? formatarMoeda(s.valor_orcado_os || 0) : '—')}
                    </td>
                    <td data-label="Período">
                      {s.data_inicio && s.data_fim ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span>{formatarDataLocal(s.data_inicio)} até {formatarDataLocal(s.data_fim)}</span>
                            {(s.data_inicio_inicial || s.data_fim_inicial) &&
                              (String(s.data_inicio_inicial).split('T')[0] !== String(s.data_inicio).split('T')[0] ||
                               String(s.data_fim_inicial).split('T')[0] !== String(s.data_fim).split('T')[0]) && (
                              <TooltipInfo texto={`Previsto inicialmente:\n${formatarDataLocal(s.data_inicio_inicial)} até ${formatarDataLocal(s.data_fim_inicial)}`} />
                            )}
                          </div>
                          {s.dias_atraso > 0 && s.status_servico !== 'pendente' && (
                            <span style={{ display: 'block', fontSize: 12, color: '#dc3545', fontWeight: 600, marginTop: 2 }}>
                              ⚠️ {s.dias_atraso} dia{s.dias_atraso > 1 ? 's' : ''} de atraso
                            </span>
                          )}
                        </div>
                      ) : '—'}
                    </td>
                    <td data-label="Status">{renderBadgeServico(s.status_servico, estaAtrasado)}</td>
                    <td data-label="Ações">
                      {!apenasVerCronograma && estaeditando && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <button className="btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={salvarEdicaoServico}>Salvar</button>
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setEditandoServico(null)}>Cancelar</button>
                        </div>
                      )}
                      {!apenasVerCronograma && !estaeditando && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {s.status_servico !== 'concluido' && (
                            <>
                              {s.status_servico === 'em_andamento' ? (
                                <button className="btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => concluirServico(s.obraservicoid)}>✅ Concluir</button>
                              ) : (() => {
                                const semDatas = !s.data_inicio || !s.data_fim;
                                const emOrcamento = obra.status_obra === 'orcamento';
                                const bloqueado = emOrcamento || semDatas;
                                const motivo = emOrcamento ? 'Inicie a obra primeiro' : semDatas ? 'Informe as datas antes de iniciar' : '';
                                return (
                                  <span title={bloqueado ? motivo : ''} style={{ display: 'block', cursor: bloqueado ? 'not-allowed' : 'default' }}>
                                    <button
                                      className="btn-primary"
                                      style={{ fontSize: 12, padding: '5px 10px', background: '#16a34a', opacity: bloqueado ? 0.45 : 1, cursor: bloqueado ? 'not-allowed' : 'pointer', pointerEvents: bloqueado ? 'none' : 'auto', width: '100%' }}
                                      onClick={() => iniciarServico(s.obraservicoid)}
                                    >▶️ Iniciar</button>
                                  </span>
                                );
                              })()}
                              <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px', opacity: obra.status_obra === 'orcamento' ? 0.45 : 1, cursor: obra.status_obra === 'orcamento' ? 'not-allowed' : 'pointer' }}
                                disabled={obra.status_obra === 'orcamento'}
                                title={obra.status_obra === 'orcamento' ? 'Disponível após iniciar a obra' : ''}
                                onClick={() => setFormAlterarPrazoServico({
                                  obraservicoid: s.obraservicoid,
                                  nova_data_inicio: s.data_inicio ? new Date(s.data_inicio).toISOString().split('T')[0] : '',
                                  nova_data_fim: s.data_fim ? new Date(s.data_fim).toISOString().split('T')[0] : '',
                                  visivel: formAlterarPrazoServico.obraservicoid === s.obraservicoid ? !formAlterarPrazoServico.visivel : true,
                                })}>
                                📅 Prazo
                              </button>
                              {estaAtrasado && (
                                <button className="btn-danger" style={{ fontSize: 12, padding: '5px 10px' }}
                                  onClick={() => setConfirmandoAtraso(confirmandoAtraso === s.obraservicoid ? null : s.obraservicoid)}>
                                  ⏰ Registrar Atraso
                                </button>
                              )}
                            </>
                          )}
                          {s.status_servico === 'concluido' && (
                            <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }}
                              onClick={() => {
                                if (obraConcluida) {
                                  toast.error('Para reabrir um serviço, reabra a obra primeiro!', { duration: 5000 });
                                  return;
                                }
                                setReabrindoServico(s.obraservicoid);
                                setJustificativaReabrirServico('');
                              }}>
                              🔄 Reabrir
                            </button>
                          )}
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => iniciarEdicaoServico(s)}>✏️ Editar</button>
                          <button className="btn-danger" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => deletarServico(s.obraservicoid)}>Remover</button>
                        </div>
                      )}

                      {formAlterarPrazoServico.visivel && formAlterarPrazoServico.obraservicoid === s.obraservicoid && !estaeditando && (
                        <div style={{ marginTop: 8, padding: 10, background: 'var(--cinza-claro)', borderRadius: 8 }}>
                          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Data início</label>
                          <input type="date" value={formAlterarPrazoServico.nova_data_inicio}
                            onChange={(e) => setFormAlterarPrazoServico({ ...formAlterarPrazoServico, nova_data_inicio: e.target.value })} />
                          <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Data fim *</label>
                          <input type="date" value={formAlterarPrazoServico.nova_data_fim}
                            onChange={(e) => setFormAlterarPrazoServico({ ...formAlterarPrazoServico, nova_data_fim: e.target.value })} />
                          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                            <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => alterarPrazoServico(s.obraservicoid)}>Salvar</button>
                            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setFormAlterarPrazoServico({ obraservicoid: null, nova_data_inicio: '', nova_data_fim: '', visivel: false })}>Cancelar</button>
                          </div>
                        </div>
                      )}

                      {confirmandoAtraso === s.obraservicoid && (
                        <div style={{ marginTop: 8, padding: 10, background: '#f8d7da', border: '1px solid #f5c2c7', borderRadius: 8 }}>
                          <p style={{ fontSize: 12, color: '#842029', fontWeight: 600, marginBottom: 6 }}>
                            ⏰ {diasEmAtraso} dia{diasEmAtraso !== 1 ? 's' : ''} em atraso
                          </p>
                          <p style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                            O prazo deste serviço será atualizado para hoje ({hojeLocal.toLocaleDateString('pt-BR')}) e os serviços seguintes serão deslocados automaticamente.
                          </p>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn-danger" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => registrarAtrasoServico(s.obraservicoid)}>Confirmar</button>
                            <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setConfirmandoAtraso(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}

                      {reabrindoServico === s.obraservicoid && (
                        <div style={{ marginTop: 8, padding: 10, background: 'rgba(251,191,36,0.13)', border: '1px solid #f59e0b', borderRadius: 8 }}>
                          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Justificativa (opcional)</label>
                          <textarea
                            value={justificativaReabrirServico}
                            onChange={e => setJustificativaReabrirServico(e.target.value)}
                            rows={2} style={{ resize: 'vertical', fontSize: 12 }}
                            placeholder="Por que está reabrindo este serviço?"
                          />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => reabrirServico(s.obraservicoid)}>Confirmar</button>
                            <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setReabrindoServico(null)}>Cancelar</button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                  {estaeditando && obra.servicos.filter(sv => sv.obraservicoid !== s.obraservicoid).length > 0 && (
                    <tr style={{ background: 'rgba(59,130,246,0.06)' }}>
                      <td colSpan={7} style={{ padding: '0 20px 14px', borderTop: 'none' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600, marginBottom: 6 }}>
                          Depende de (predecessores):
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 140, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-card)' }}>
                          {obra.servicos.filter(sv => sv.obraservicoid !== s.obraservicoid).map((sv, idx) => (
                            <label key={sv.obraservicoid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)', background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.03)' }}>
                              <input
                                type="checkbox"
                                style={{ width: 15, height: 15, flexShrink: 0, accentColor: '#3b82f6', cursor: 'pointer', margin: 0, display: 'block' }}
                                checked={(editandoServico.predecessores || []).map(Number).includes(Number(sv.obraservicoid))}
                                onChange={e => {
                                  const id = Number(sv.obraservicoid);
                                  const preds = e.target.checked
                                    ? [...(editandoServico.predecessores || []), id]
                                    : (editandoServico.predecessores || []).filter(p => p !== id);
                                  setEditandoServico({ ...editandoServico, predecessores: preds });
                                }}
                              />
                              <span style={{ fontSize: 12, color: 'var(--text-primary)', flex: 1 }}>{sv.nome_servico}</span>
                              {sv.data_fim && (
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                  até {new Date(sv.data_fim).toISOString().split('T')[0].split('-').reverse().join('/')}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {(!obra.servicos || obra.servicos.length === 0) && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>Nenhum serviço adicionado</td></tr>
            )}
          </tbody>
        </table>

        {podeVerFinanceiro && (
          <div style={{ textAlign: 'right', marginTop: 12, fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
            Total Orçado: {formatarMoeda(totalOrcado)}
          </div>
        )}
        </>
        )}
      </div>

      {/* pagamentos */}
      {podeVerFinanceiro && <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3>💰 Pagamentos</h3>
          <button className="btn-secondary" onClick={() => setMostrarFormPagamento(!mostrarFormPagamento)}>
            {mostrarFormPagamento ? 'Cancelar' : '+ Adicionar Método'}
          </button>
        </div>

        {mostrarFormPagamento && (
          <div style={{ marginBottom: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
            <select value={formPagamento.forma_pagamento}
              onChange={(e) => setFormPagamento({ ...formPagamento, forma_pagamento: e.target.value, parcelas: '', dia_vencimento: '', prazo_pagamento: '' })}>
              <option value="">Forma de pagamento *</option>
              {formasPagamento.map((f) => (
                <option key={f.formapagamentoid || f.nome} value={f.nome}>{f.nome}</option>
              ))}
            </select>
            <input placeholder="Valor total neste método * (Ex: R$ 5.000,00)"
              value={formPagamento.valor_total_formatado} onChange={handleValorPagamento} />
            {formaAtual.parcelado && (
              <input type="number" placeholder="Número de parcelas *" min={2}
                value={formPagamento.parcelas}
                onChange={(e) => setFormPagamento({ ...formPagamento, parcelas: e.target.value })} />
            )}
            {formaAtual.tem_dia_vencimento && (
              <input type="number" placeholder="Dia do mês para pagamento * (1 a 31)" min={1} max={31}
                value={formPagamento.dia_vencimento}
                onChange={(e) => setFormPagamento({ ...formPagamento, dia_vencimento: e.target.value })} />
            )}
            {!formaAtual.tem_dia_vencimento && formPagamento.forma_pagamento && (
              <>
                <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Prazo de pagamento *</label>
                <input type="date" value={formPagamento.prazo_pagamento}
                  onChange={(e) => setFormPagamento({ ...formPagamento, prazo_pagamento: e.target.value })} />
              </>
            )}
            <button className="btn-primary" onClick={salvarPagamento}>Salvar Método</button>
          </div>
        )}

        {pagamentoData?.pagamentos?.map((p) => {
          const progresso = calcularProgressoMetodo(p.forma_pagamento, p.valor_total);
          const recebidoMetodo = pagamentoData.recebimentos
            .filter((r) => r.forma_pagamento === p.forma_pagamento)
            .reduce((acc, r) => acc + parseFloat(r.valor_recebido), 0);
          const estaEditandoPgto = editandoPagamento?.pagamentoid === p.pagamentoid;
          const formaEdicaoPgto = estaEditandoPgto ? getFormaFlags(editandoPagamento.forma_pagamento) : null;

          return (
            <div key={p.pagamentoid} style={{ border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 12 }}>
              {estaEditandoPgto ? (
                <div>
                  <h4 style={{ marginBottom: 12 }}>✏️ Editar Método de Pagamento</h4>
                  <select value={editandoPagamento.forma_pagamento}
                    onChange={(e) => setEditandoPagamento({ ...editandoPagamento, forma_pagamento: e.target.value, parcelas: '', dia_vencimento: '', prazo_pagamento: '' })}>
                    {formasPagamento.map((f) => (
                      <option key={f.formapagamentoid || f.nome} value={f.nome}>{f.nome}</option>
                    ))}
                  </select>
                  <input placeholder="Valor total * (Ex: R$ 5.000,00)" value={editandoPagamento.valor_total_formatado} onChange={handleValorEdicaoPagamento} />
                  {formaEdicaoPgto?.parcelado && (
                    <input type="number" placeholder="Número de parcelas *" min={2}
                      value={editandoPagamento.parcelas}
                      onChange={(e) => setEditandoPagamento({ ...editandoPagamento, parcelas: e.target.value })} />
                  )}
                  {formaEdicaoPgto?.tem_dia_vencimento && (
                    <input type="number" placeholder="Dia do mês (1 a 31) *" min={1} max={31}
                      value={editandoPagamento.dia_vencimento}
                      onChange={(e) => setEditandoPagamento({ ...editandoPagamento, dia_vencimento: e.target.value })} />
                  )}
                  {formaEdicaoPgto && !formaEdicaoPgto.tem_dia_vencimento && (
                    <>
                      <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Prazo de pagamento *</label>
                      <input type="date" value={editandoPagamento.prazo_pagamento}
                        onChange={(e) => setEditandoPagamento({ ...editandoPagamento, prazo_pagamento: e.target.value })} />
                    </>
                  )}
                  <textarea
                    placeholder="Justificativa da edição (opcional)"
                    value={editandoPagamento.justificativa}
                    onChange={(e) => setEditandoPagamento({ ...editandoPagamento, justificativa: e.target.value })}
                    rows={2} style={{ resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" onClick={salvarEdicaoPagamento}>Salvar</button>
                    <button className="btn-secondary" onClick={() => setEditandoPagamento(null)}>Cancelar</button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <strong>{p.forma_pagamento}</strong>
                      {p.parcelas && <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>, {p.parcelas}x</span>}
                      {p.dia_vencimento && <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>, todo dia {p.dia_vencimento}</span>}
                      {p.prazo_pagamento && (
                        <span style={{ marginLeft: 8, fontSize: 13, color: '#666' }}>
                          , prazo: {formatarDataLocal(p.prazo_pagamento)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn-secondary" onClick={() => iniciarEdicaoPagamento(p)}>✏️ Editar</button>
                      <button className="btn-danger" onClick={() => deletarMetodoPagamento(p.pagamentoid)}>Remover</button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <span>Recebido: {formatarMoeda(recebidoMetodo)}</span>
                    <span>Total: {formatarMoeda(p.valor_total || 0)}</span>
                  </div>
                  <div style={{ background: '#e0e0e0', borderRadius: 8, height: 10 }}>
                    <div style={{ width: `${progresso}%`, background: progresso >= 100 ? 'var(--verde)' : 'var(--azul-medio)', height: 10, borderRadius: 8, transition: 'width 0.3s' }} />
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4, fontWeight: 600, color: progresso >= 100 ? 'var(--verde)' : 'var(--azul-medio)' }}>
                    {progresso}% recebido
                  </div>
                </>
              )}
            </div>
          );
        })}

        {pagamentoData && multiplosMetodos && (
          <div style={{ marginTop: 8, padding: 16, background: 'var(--cinza-claro)', borderRadius: 10 }}>
            <strong>Progresso Geral</strong>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 8, marginBottom: 4 }}>
              <span>Total recebido: {formatarMoeda(pagamentoData.total_recebido || 0)}</span>
              <span>Valor fechado: {formatarMoeda(obra.valor_fechado_obra || 0)}</span>
            </div>
            <div style={{ background: '#e0e0e0', borderRadius: 8, height: 12 }}>
              <div style={{ width: `${progressoGeral}%`, background: progressoGeral >= 100 ? 'var(--verde)' : 'var(--azul-medio)', height: 12, borderRadius: 8, transition: 'width 0.3s' }} />
            </div>
            <div style={{ textAlign: 'right', fontSize: 13, marginTop: 4, fontWeight: 600, color: progressoGeral >= 100 ? 'var(--verde)' : 'var(--azul-medio)' }}>
              {progressoGeral}% recebido
            </div>
          </div>
        )}

        {pagamentoData && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 12px' }}>
              <h3>Recebimentos</h3>
              <button className="btn-secondary" onClick={() => setMostrarFormRecebimento(!mostrarFormRecebimento)}>
                {mostrarFormRecebimento ? 'Cancelar' : '+ Registrar Recebimento'}
              </button>
            </div>

            {mostrarFormRecebimento && (
              <div style={{ marginBottom: 16, padding: 16, background: 'var(--cinza-claro)', borderRadius: 8 }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <input type="date" value={formRecebimento.data_recebimento}
                    onChange={(e) => setFormRecebimento({ ...formRecebimento, data_recebimento: e.target.value })}
                    style={{ flex: 1 }} />
                  <input placeholder="Valor recebido (Ex: R$ 1.000,00)" value={formRecebimento.valor_recebido_formatado}
                    onChange={handleValorRecebimento} style={{ flex: 1 }} />
                  {multiplosMetodos && (
                    <select value={formRecebimento.forma_pagamento}
                      onChange={(e) => setFormRecebimento({ ...formRecebimento, forma_pagamento: e.target.value })}
                      style={{ flex: 1 }}>
                      <option value="">Método utilizado *</option>
                      {pagamentoData.pagamentos.map((p) => (
                        <option key={p.pagamentoid} value={p.forma_pagamento}>
                          {p.forma_pagamento}{p.parcelas ? ` (${p.parcelas}x)` : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  <input placeholder="Observação" value={formRecebimento.observacao}
                    onChange={(e) => setFormRecebimento({ ...formRecebimento, observacao: e.target.value })}
                    style={{ flex: 2 }} />
                </div>
                <button className="btn-primary" style={{ marginTop: 12 }} onClick={registrarRecebimento}>Salvar</button>
              </div>
            )}

            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Valor</th>
                  <th>Método</th>
                  <th>Observação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentoData.recebimentos?.map((r) => {
                  const estaEditandoRec = editandoRecebimento?.recebimentoid === r.recebimentoid;
                  return estaEditandoRec ? (
                    <tr key={r.recebimentoid} style={{ background: '#f0f7ff' }}>
                      <td data-label="Data"><input type="date" value={editandoRecebimento.data_recebimento} onChange={e => setEditandoRecebimento({ ...editandoRecebimento, data_recebimento: e.target.value })} style={{ marginBottom: 0 }} /></td>
                      <td data-label="Valor"><input value={editandoRecebimento.valor_recebido_formatado} onChange={handleValorEdicaoRecebimento} style={{ marginBottom: 0 }} /></td>
                      <td data-label="Método">{r.forma_pagamento || '—'}</td>
                      <td data-label="Observação"><input value={editandoRecebimento.observacao} onChange={e => setEditandoRecebimento({ ...editandoRecebimento, observacao: e.target.value })} style={{ marginBottom: 0 }} /></td>
                      <td data-label="Ações">
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-primary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={salvarEdicaoRecebimento}>Salvar</button>
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => setEditandoRecebimento(null)}>Cancelar</button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={r.recebimentoid}>
                      <td data-label="Data">{formatarDataLocal(r.data_recebimento)}</td>
                      <td data-label="Valor">{formatarMoeda(r.valor_recebido)}</td>
                      <td data-label="Método">{r.forma_pagamento || '—'}</td>
                      <td data-label="Observação">{r.observacao || '—'}</td>
                      <td data-label="Ações">
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn-secondary" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => iniciarEdicaoRecebimento(r)}>✏️ Editar</button>
                          <button className="btn-danger" onClick={() => deletarRecebimento(r.recebimentoid)}>Remover</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!pagamentoData.recebimentos || pagamentoData.recebimentos.length === 0) && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>Nenhum recebimento registrado</td></tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>}

      {/* histórico de prazos */}
      {!apenasVerCronograma && <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>📅 Histórico</h3>
          <button className="btn-secondary" onClick={() => setMostrarHistorico(!mostrarHistorico)}>
            {mostrarHistorico ? 'Ocultar' : 'Ver Histórico'}
          </button>
        </div>

        {mostrarHistorico && (
          <table style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Data</th>
                <th>Usuário</th>
                <th>Ação</th>
                <th>Prazo Anterior</th>
                <th>Novo Prazo</th>
                <th>Justificativa</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h) => (
                <tr key={h.historicoprazoid}>
                  <td data-label="Tipo"><span style={{ background: '#e0e7ff', color: '#3730a3', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{h.tipo || 'Prazo Obra'}</span></td>
                  <td data-label="Data">{formatarDataLocal(h.created_at)}</td>
                  <td data-label="Usuário" style={{ fontSize: 12, color: '#6b7280' }}>{h.usuario_nome || '—'}</td>
                  <td data-label="Ação">{h.acao || '—'}</td>
                  <td data-label="Prazo Anterior">{formatarDataLocal(h.prazo_anterior)}</td>
                  <td data-label="Novo Prazo">{formatarDataLocal(h.novo_prazo)}</td>
                  <td data-label="Justificativa">{h.justificativa || '—'}</td>
                </tr>
              ))}
              {historico.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>Nenhum registro encontrado</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>}

      {/* Modal de preview de imagem/PDF */}
      {previewProjeto && (
        <div className="modal-overlay" onClick={() => setPreviewProjeto(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: 12, padding: 16,
            maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#111' }}>{previewProjeto.nome_original}</span>
              <button onClick={() => setPreviewProjeto(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <ArquivoPreview
              projetoid={previewProjeto.projetoid}
              extensao={previewProjeto.extensao}
              nomeOriginal={previewProjeto.nome_original}
              style={{
                maxWidth: '80vw', maxHeight: '75vh', borderRadius: 8,
                ...(['png', 'jpg', 'jpeg'].includes(previewProjeto.extensao)
                  ? { objectFit: 'contain', display: 'block' }
                  : { width: '80vw', height: '75vh' })
              }}
            />
          </div>
        </div>
      )}

      {confirmDeleteProjeto && (
        <div className="modal-overlay" onClick={() => setConfirmDeleteProjeto(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>🗑️ Remover arquivo</h3>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>
              Deseja remover <strong>"{confirmDeleteProjeto.nome_original}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn-secondary" onClick={() => setConfirmDeleteProjeto(null)}>Cancelar</button>
              <button className="btn-danger" onClick={() => deletarProjeto(confirmDeleteProjeto.projetoid)}>Remover</button>
            </div>
          </div>
        </div>
      )}

      {avisoAdiantamento && (
        <div className="modal-overlay" onClick={() => setAvisoAdiantamento(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>🎉 Serviço concluído antes do prazo!</h3>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>
              O prazo previsto era <strong>{avisoAdiantamento.data_fim_prevista?.split('-').reverse().join('/')}</strong>, mas foi concluído em <strong>{avisoAdiantamento.data_fim_real?.split('-').reverse().join('/')}</strong>.
            </p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.6 }}>
              Os seguintes serviços dependentes poderiam ser adiantados:
            </p>
            <ul style={{ margin: '8px 0 16px 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {avisoAdiantamento.dependentes?.map(d => (
                <li key={d.obraservicoid}>{d.nome_servico}, previsto para {d.data_inicio?.split('T')[0].split('-').reverse().join('/')}</li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Deseja adiantar os prazos desses serviços automaticamente?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn-secondary" onClick={() => setAvisoAdiantamento(null)}>Não, manter</button>
              <button className="btn-primary" onClick={async () => {
                try {
                  await api.post(`/obras-servicos/${avisoAdiantamento.servicoid}/aplicar-adiantamento`);
                  setAvisoAdiantamento(null);
                  carregarObra();
                  toast.success('Prazos adiantados!');
                } catch {
                  toast.error('Erro ao adiantar prazos.');
                }
              }}>Sim, adiantar</button>
            </div>
          </div>
        </div>
      )}

      {confirmarExclusaoServico && (
        <div className="modal-overlay" onClick={() => setConfirmarExclusaoServico(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>🔗 Serviço com dependentes</h3>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>
              Este serviço é predecessor de:
            </p>
            <ul style={{ margin: '8px 0 16px 18px', fontSize: 13, color: 'var(--text-primary)' }}>
              {confirmarExclusaoServico.dependentes.map(d => (
                <li key={d.obraservicoid}>{d.nome_servico}</li>
              ))}
            </ul>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Ao excluir, esses vínculos serão removidos automaticamente. Deseja continuar?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn-secondary" onClick={() => setConfirmarExclusaoServico(null)}>Cancelar</button>
              <button className="btn-danger" onClick={() => executarExclusaoServico(confirmarExclusaoServico.id)}>Remover mesmo assim</button>
            </div>
          </div>
        </div>
      )}

      {avisoConcluirSemPgto && (
        <div className="modal-overlay" onClick={() => setAvisoConcluirSemPgto(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>⚠️ Sem pagamento informado</h3>
            <p style={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>
              Esta obra não possui nenhuma forma de pagamento cadastrada. Deseja concluir mesmo assim?
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn-secondary" onClick={() => setAvisoConcluirSemPgto(false)}>Cancelar</button>
              <button className="btn-primary" onClick={concluirObra}>Concluir mesmo assim</button>
            </div>
          </div>
        </div>
      )}

      {creditoPrompt && (
        <div className="modal-overlay" onClick={() => setCreditoPrompt(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Limite de obras atingido</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{creditoPrompt.mensagem}</p>
            {creditoPrompt.onUsar ? (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 16 }}>
                  Você tem <strong>{creditoPrompt.saldoCreditos}</strong> crédito{creditoPrompt.saldoCreditos > 1 ? 's' : ''} avulso{creditoPrompt.saldoCreditos > 1 ? 's' : ''} {creditoPrompt.saldoCreditos > 1 ? 'disponíveis' : 'disponível'}. Deseja usar 1 crédito para liberar esta obra?
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setCreditoPrompt(null)}>Cancelar</button>
                  <button className="btn-primary" onClick={creditoPrompt.onUsar}>Usar 1 crédito</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 16 }}>
                  Você não possui créditos avulsos. Cada crédito custa R$ {parseFloat(creditoPrompt.precoCredito || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} e libera 1 obra extra, permanentemente. Compre na aba "Plano".
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setCreditoPrompt(null)}>Fechar</button>
                  <button className="btn-primary" onClick={() => { setCreditoPrompt(null); navigate('/plano'); }}>Ir para Plano</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ObraDetalhe;
